import {
    createFileRoute,
    Link,
    useNavigate,
    useRouter,
} from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    AlertTriangle,
    ArrowLeft,
    Bot,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Copy,
    ExternalLink,
    GitBranch,
    Link2,
    Loader2,
    PanelRightClose,
    PanelRightOpen,
    RotateCcw,
    Send,
    Sparkles,
    User,
    Wrench,
    X,
} from 'lucide-react';
import {
    Fragment,
    type ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { AdminAgentChatPanel } from '@apps/web/components/admin/admin_agent_chat_panel';
import { assertAdminAgentPageContext } from '@apps/web/components/admin/admin_agent_page_context';
import { AdminPhoebeChatWrapper } from '@apps/web/components/admin/admin_phoebe_chat_wrapper';
import { AgentTraceMessageFeedbackIndicator } from '@apps/web/components/admin/agent_traces/agent_trace_message_feedback';
import { ModelSpaceContent } from '@apps/web/components/admin/agent_traces/model_space_content';
import { RunMetricsCard } from '@apps/web/components/admin/agent_traces/run_metrics_card';
import { ListNavControls } from '@apps/web/components/admin/list_nav_controls';
import {
    DETAIL_TWO_PANE_SCROLL_REGION_CLASS_NAME,
    DETAIL_TWO_PANE_SHELL_CLASS_NAME,
    DetailTwoPaneShell,
    DetailTwoPaneShellNotFound,
} from '@apps/web/components/admin/trace_views/detail_two_pane_shell';
import { useAdminAgentChatSession } from '@apps/web/components/admin/use_admin_agent_chat_session';
import { type ShiftActionPolicy } from '@apps/web/components/phoebe_chat/assistant_response_types';
import {
    buildPhoebeChatDebugSnapshot,
    type PhoebeChatDebugSnapshot,
} from '@apps/web/components/phoebe_chat_debug_snapshot';
import {
    getToolEntryInspectionId,
    type MessageEntry,
    PhoebeAgentEventFeedChatSurface,
    type RenderTimelineEntry,
    usePhoebeAgentEventFeedChatViewState,
} from '@apps/web/components/phoebe_chat_view';
import { IdentifierRedactionProvider } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { buildAgentTraceChip } from '@apps/web/features/admin_agent_dock/chip_context_providers';
import { useRouteChipPublisher } from '@apps/web/features/admin_agent_dock/route_chip_source';
import { useGlobalHotkey } from '@apps/web/hooks/use_global_hotkey';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatElapsedMs } from '@apps/web/lib/format_elapsed';
import {
    getApiErrorDetail,
    isTransparentRateLimitError,
} from '@apps/web/lib/known_api_errors';
import { cn } from '@apps/web/lib/utils';
import { isDefaultAdminAgentOrganization } from '@apps/web/routes/_app/admin/agent/admin_agent_helpers';
import { AdminAgentTierPicker } from '@apps/web/routes/_app/admin/agent/admin_agent_tier_picker';
import {
    createAdminMessageArtifactRenderer,
    createAdminToolArtifactRenderer,
} from '@apps/web/routes/_app/admin/agent/artifacts/list';
import { AgentTraceAgentGraph } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_agent_graph';
import {
    AGENT_TRACE_JUDGE_MODEL_OPTIONS,
    DEFAULT_AGENT_TRACE_JUDGE_MODEL,
    getAgentTraceJudgeModelLabel,
    getAgentTraceJudgeQualityBadgeVariant,
    isAgentTraceJudgeQualityGrade,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_judge';
import { resolveCurrentAgentTracePageContext } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_page_context';
import {
    AGENT_TRACE_DETAIL_POLL_INTERVAL_MS,
    createAgentTracePollingOwnerId,
    releaseAgentTracePollingLease,
    tryAcquireAgentTracePollingLease,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_polling_lease';
import {
    AgentTracePrimaryTabs,
    CONVERSATION_AGENT_TRACE_PRIMARY_TABS,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_primary_tabs';
import {
    type AgentTraceRunRelationship,
    formatRunRelationshipLabel,
    formatRunTypeLabel,
    RelatedRunLink,
    SubagentRunsPanel,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_related_runs';
import { AgentTraceTranscript } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_transcript_entry';
import { AnnotationCombobox } from '@apps/web/routes/_app/admin/agent-traces/annotation_combobox';
import { AsideQuestionButton } from '@apps/web/routes/_app/admin/agent-traces/aside_question_dialog';
import { BraintrustExecutionLinks } from '@apps/web/routes/_app/admin/agent-traces/braintrust_execution_links';
import { AgentTraceEpisodesPanel } from '@apps/web/routes/_app/admin/agent-traces/episode_linkage_panel';
import {
    OwnerSelect,
    useOwnerCandidates,
} from '@apps/web/routes/_app/admin/agent-traces/owner_select';
import { SandboxFileExplorer } from '@apps/web/routes/_app/admin/agent-traces/sandbox_file_explorer';
import {
    agentTraceDetailSearchSchema,
    detailSearchToListSearch,
    detailSearchWithAdminAgentRun,
    getDetailAdminAgentRunIdForTrace,
    REVIEW_STATUS_LABELS,
} from '@apps/web/routes/_app/admin/agent-traces/search_params';
import { getAgentTraceExecutionBackendLabel } from '@apps/web/routes/_app/admin/agent-traces/trace_table';
import { useTraceListNavigation } from '@apps/web/routes/_app/admin/agent-traces/use_trace_list_navigation';
import {
    updateAgentTraceOwner,
    useAdminAgentRun,
    useAdminAgentTrace,
    useAdminAgentTraceAnnotationGroups,
    useAdminAgentTraceSummary,
    useAgentModelOptions,
    useAllAdminOrganizations,
    useJoinedOutreach,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgentTraceDetail,
    type AgentTraceItemRead,
    type AgentTraceTurnRead,
    ApiError,
    type Mode,
} from '@libraries/typescript/api_client';
import { generateUuid } from '@libraries/typescript/utilities';

// Re-exported from the shared shell so the class strings have a single source
// of truth while the detail scroll-layout contract test keeps importing them
// from this route.
export const AGENT_TRACE_DETAIL_PAGE_SHELL_CLASS_NAME =
    DETAIL_TWO_PANE_SHELL_CLASS_NAME;
export const AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME =
    DETAIL_TWO_PANE_SCROLL_REGION_CLASS_NAME;
const DETAIL_PANEL_HEIGHT_CLASS = 'min-h-[36rem] lg:h-full lg:min-h-0';
const EMBEDDED_CHAT_HEIGHT_CLASS = 'min-h-[28rem] lg:h-full lg:min-h-0';
const AGENT_TRACE_LLM_JUDGE_REVIEW_SOURCE = 'agent_trace_llm_judge';
const ADMIN_AGENT_TRACE_SIDEBAR_COLLAPSE_KEY_PREFIX =
    'phoebe.admin_agent.trace_sidebar';

export function getOutreachMonitoringUpdateErrorMessage(
    error: unknown,
): string | null {
    if (isTransparentRateLimitError(error)) {
        return null;
    }
    const guardrailDetail =
        error instanceof ApiError && error.status === 429
            ? getApiErrorDetail(error)
            : null;
    if (guardrailDetail !== null) {
        return guardrailDetail;
    }
    return error instanceof Error
        ? error.message
        : 'Failed to update monitoring.';
}

export type PendingAdminAgentPrompt = {
    readonly conversationId: string;
    readonly prompt: string;
    readonly attempt: number;
};

export function clearHandledPendingAdminAgentPrompt(
    current: PendingAdminAgentPrompt | null,
    handledPrompt: PendingAdminAgentPrompt,
): PendingAdminAgentPrompt | null {
    if (
        current?.conversationId === handledPrompt.conversationId &&
        current.prompt === handledPrompt.prompt
    ) {
        return null;
    }
    return current;
}

type TraceSidebarCollapseState = {
    readonly storageKey: string | null;
    readonly isOpen: boolean;
};

type TraceDiagnosticsTab = 'review' | 'admin-agent' | 'llm-judge';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function collapseStorageKey(userId: string | null | undefined): string | null {
    if (!userId) {
        return null;
    }
    return `${ADMIN_AGENT_TRACE_SIDEBAR_COLLAPSE_KEY_PREFIX}:${userId}`;
}

function readTraceSidebarOpen(storageKey: string): boolean {
    if (typeof window === 'undefined') {
        return true;
    }
    return window.localStorage.getItem(storageKey) !== 'collapsed';
}

function writeTraceSidebarOpen(storageKey: string, isOpen: boolean): void {
    if (typeof window === 'undefined') {
        return;
    }
    window.localStorage.setItem(storageKey, isOpen ? 'open' : 'collapsed');
}

type AgentTraceImessageDebug = NonNullable<AgentTraceDetail['imessage_debug']>;
type AgentTraceImessageDebugRun = AgentTraceImessageDebug['topic_runs'][number];
type AgentTraceImessageDebugTurn = AgentTraceImessageDebug['turns'][number];
type AgentTraceImessageContext = Pick<
    AgentTraceDetail,
    'imessage_debug' | 'phoebe_run_id'
>;

/** The "big" iMessage chat trace: the run bound to the surface thread. */
export function isImessageRouterTrace(
    trace: AgentTraceImessageContext,
): boolean {
    return (
        trace.phoebe_run_id !== null &&
        trace.imessage_debug?.router_run?.id === trace.phoebe_run_id
    );
}

function imessageSubagentRunLabel(run: AgentTraceImessageDebugRun): string {
    return run.topic_key ?? run.title ?? `Run ${run.id.slice(0, 8)}`;
}

/**
 * Chat markers for inbound iMessages the router sent to another run. The
 * router trace's own event feed never sees those messages, so the "big"
 * iMessage chat interleaves them as user bubbles linking to the subagent
 * trace that handled each one.
 */
export function getImessageRoutedMessageEntries(
    trace: AgentTraceImessageContext,
): Array<MessageEntry> {
    const debug = trace.imessage_debug;
    if (!debug || !isImessageRouterTrace(trace)) {
        return [];
    }
    const runsById = new Map(debug.topic_runs.map((run) => [run.id, run]));
    const entries: Array<MessageEntry> = [];
    for (const turn of debug.turns) {
        if (
            !turn.selected_run_id ||
            turn.selected_run_id === trace.phoebe_run_id
        ) {
            continue;
        }
        const subagentRun = runsById.get(turn.selected_run_id);
        entries.push({
            kind: 'message',
            id: `imessage-routed-turn-${turn.id}`,
            role: 'user',
            content: turn.inbound_text ?? '(no message text)',
            createdAt: turn.created_at,
            destinationLinks: subagentRun?.conversation_id
                ? [
                      {
                          label: `iMessage subagent: ${imessageSubagentRunLabel(
                              subagentRun,
                          )}`,
                          url: `/admin/agent-traces/${subagentRun.conversation_id}`,
                      },
                  ]
                : undefined,
        });
    }
    return entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Insert routed-message markers into the rendered timeline by timestamp.
 * Timeline entries without their own timestamp (e.g. completed-run groups)
 * keep the position of the previous timestamped entry. Timestamp ties put
 * the routed inbound message first — it triggered whatever follows.
 */
export function mergeImessageRoutedEntriesIntoTimeline(
    timelineEntries: ReadonlyArray<RenderTimelineEntry>,
    routedEntries: ReadonlyArray<MessageEntry>,
): Array<RenderTimelineEntry> {
    if (routedEntries.length === 0) {
        return [...timelineEntries];
    }
    const pending = [...routedEntries].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
    );
    const merged: Array<RenderTimelineEntry> = [];
    let nextRouted = pending.shift();
    let lastEntryTimeMs = Number.NEGATIVE_INFINITY;
    for (const entry of timelineEntries) {
        if ('createdAt' in entry && typeof entry.createdAt === 'string') {
            lastEntryTimeMs = new Date(entry.createdAt).getTime();
        }
        while (
            nextRouted !== undefined &&
            new Date(nextRouted.createdAt).getTime() <= lastEntryTimeMs
        ) {
            merged.push(nextRouted);
            nextRouted = pending.shift();
        }
        merged.push(entry);
    }
    while (nextRouted !== undefined) {
        merged.push(nextRouted);
        nextRouted = pending.shift();
    }
    return merged;
}

function AdminAgentTraceEventChat({
    focusAgentItemId,
    trace,
}: {
    readonly focusAgentItemId: string | null;
    readonly trace: AgentTraceDetail;
}) {
    const navigate = useNavigate();
    const [showAllInlineDebugEvents, setShowAllInlineDebugEvents] =
        useState(false);
    const { data: outreach, mutate: mutateOutreach } = useJoinedOutreach(
        trace.organization_id,
        trace.outreach_id,
        { reason: 'admin-agent-trace-monitoring-status' },
        trace.mode,
    );
    const [isStoppingMonitoring, setIsStoppingMonitoring] = useState(false);
    const [isConfiguringMonitoring, setIsConfiguringMonitoring] =
        useState(false);
    const outreachMetadata = isRecord(outreach?.outreach_metadata)
        ? outreach.outreach_metadata
        : null;
    const monitoringScheduleMetadata = isRecord(
        outreachMetadata?.['monitoring_schedule'],
    )
        ? outreachMetadata['monitoring_schedule']
        : null;
    const isMonitoringOutreach = monitoringScheduleMetadata !== null;
    const stopOutreachMonitoring = useCallback(async () => {
        if (
            trace.phoebe_run_id === null ||
            trace.organization_id === null ||
            isStoppingMonitoring
        ) {
            return;
        }

        setIsStoppingMonitoring(true);
        const operationId = generateUuid();
        try {
            const postStop = getApiClient().post as (args: {
                url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/stop';
                pathParams: {
                    organization_id: string;
                    run_id: string;
                };
                mode: Mode;
                requestId: string;
            }) => Promise<unknown>;
            await postStop({
                url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/stop',
                pathParams: {
                    organization_id: trace.organization_id,
                    run_id: trace.phoebe_run_id,
                },
                mode: trace.mode,
                requestId: operationId,
            });
            void mutateOutreach();
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to stop monitoring.',
                    { persistNotification: false },
                );
            }
        } finally {
            setIsStoppingMonitoring(false);
        }
    }, [
        isStoppingMonitoring,
        mutateOutreach,
        trace.mode,
        trace.organization_id,
        trace.phoebe_run_id,
    ]);
    const configureOutreachMonitoring = useCallback(
        async (payload: {
            readonly conditions: Array<string>;
        }): Promise<boolean> => {
            if (
                trace.phoebe_run_id === null ||
                trace.organization_id === null ||
                isConfiguringMonitoring
            ) {
                return false;
            }

            setIsConfiguringMonitoring(true);
            const operationId = generateUuid();
            try {
                const postConfigure = getApiClient().post as (args: {
                    url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/configure';
                    pathParams: {
                        organization_id: string;
                        run_id: string;
                    };
                    body: {
                        conditions: Array<string>;
                        client_operation_id: string;
                    };
                    mode: Mode;
                    requestId: string;
                }) => Promise<unknown>;
                await postConfigure({
                    url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/configure',
                    pathParams: {
                        organization_id: trace.organization_id,
                        run_id: trace.phoebe_run_id,
                    },
                    body: {
                        conditions: payload.conditions,
                        client_operation_id: operationId,
                    },
                    mode: trace.mode,
                    requestId: operationId,
                });
                void mutateOutreach();
                toast.success('Monitoring updated', {
                    persistNotification: false,
                });
                return true;
            } catch (error) {
                const message = getOutreachMonitoringUpdateErrorMessage(error);
                if (message !== null) {
                    toast.error(message, { persistNotification: false });
                }
                return false;
            } finally {
                setIsConfiguringMonitoring(false);
            }
        },
        [
            isConfiguringMonitoring,
            mutateOutreach,
            trace.mode,
            trace.organization_id,
            trace.phoebe_run_id,
        ],
    );
    const monitoringControls =
        trace.phoebe_run_id !== null && isMonitoringOutreach
            ? {
                  isStopping: isStoppingMonitoring,
                  isConfiguring: isConfiguringMonitoring,
                  onStop: () => {
                      void stopOutreachMonitoring();
                  },
                  onConfigure: (payload: {
                      readonly conditions: Array<string>;
                  }) => configureOutreachMonitoring(payload),
              }
            : null;
    const shouldShowWorkflowActivitySurface =
        trace.run_state?.activity_type === 'workflow';
    // Mirror the live outreach chat so the admin trace suppresses the same
    // shift cards and "create outreach" action.
    const shiftActionPolicy: ShiftActionPolicy = useMemo(() => {
        const isOutreachTarget = trace.outreach_id !== null;
        return {
            outreachStarted:
                isOutreachTarget &&
                outreach?.status !== undefined &&
                outreach?.status !== null &&
                outreach.status !== 'pending',
            showCreateOutreachAction: !isOutreachTarget,
            currentOutreachShiftIds: isOutreachTarget
                ? (outreach?.shift_ids ?? [])
                : [],
        };
    }, [trace.outreach_id, outreach?.status, outreach?.shift_ids]);
    const state = usePhoebeAgentEventFeedChatViewState({
        events: trace.events,
        pendingEvents: trace.pending_events,
        collapseInlineDebugEvents: !showAllInlineDebugEvents,
        fallbackRunStatus: trace.run_state?.status ?? null,
        phoebeAgentRunId: trace.phoebe_run_id,
        shouldShowWorkflowActivitySurface,
        // Every agent trace is a general event-agent run, so always render the
        // consolidated execution-plan card instead of raw per-tool steps.
        showGeneralOutreachProgressCards: true,
        showInlineDebugEvents: true,
    });
    const routedImessageEntries = useMemo(
        () => getImessageRoutedMessageEntries(trace),
        [trace],
    );
    const chatViewState = useMemo(
        () =>
            routedImessageEntries.length === 0
                ? state
                : {
                      ...state,
                      timelineEntries: mergeImessageRoutedEntriesIntoTimeline(
                          state.timelineEntries,
                          routedImessageEntries,
                      ),
                  },
        [routedImessageEntries, state],
    );
    const messageFeedbackByItemId = useMemo(
        () =>
            new Map(
                (trace.message_feedback ?? []).map((feedback) => [
                    feedback.agent_item_id,
                    feedback,
                ]),
            ),
        [trace.message_feedback],
    );
    const renderMessageFeedback = useCallback(
        (message: MessageEntry) => {
            const feedback = message.agentItemId
                ? messageFeedbackByItemId.get(message.agentItemId)
                : undefined;
            if (!feedback) {
                return null;
            }
            return (
                <AgentTraceMessageFeedbackIndicator
                    goodCount={feedback.good_count}
                    badCount={feedback.bad_count}
                />
            );
        },
        [messageFeedbackByItemId],
    );
    const renderAssistantMessageActions = useCallback(
        (message: MessageEntry) => {
            if (!message.agentItemId) {
                return null;
            }
            return (
                <AsideQuestionButton
                    conversationId={trace.id}
                    agentItemId={message.agentItemId}
                    entityAliases={trace.entity_aliases}
                    organizationId={trace.organization_id}
                    mode={trace.mode}
                />
            );
        },
        [trace.entity_aliases, trace.id, trace.mode, trace.organization_id],
    );
    const handleOpenSubagentRun = useCallback(
        (childRunId: string) => {
            const childRun = trace.child_runs?.find(
                (candidate) => candidate.run_id === childRunId,
            );
            if (!childRun) {
                return;
            }
            if (childRun.conversation_id) {
                void navigate({
                    to: '/admin/agent-traces/$conversationId',
                    params: { conversationId: childRun.conversation_id },
                });
                return;
            }
            void navigate({
                to: '/admin/agent-traces/run/$runId',
                params: { runId: childRun.run_id },
            });
        },
        [navigate, trace.child_runs],
    );

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="border-border flex h-10 shrink-0 items-center justify-end border-b px-3">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-pressed={showAllInlineDebugEvents}
                    onClick={() =>
                        setShowAllInlineDebugEvents((current) => !current)
                    }
                    className="h-7 px-2 text-xs"
                >
                    {showAllInlineDebugEvents
                        ? 'Hide all events'
                        : 'Show all events'}
                </Button>
            </div>
            <div className="min-h-0 flex-1">
                <IdentifierRedactionProvider redact={false}>
                    <PhoebeAgentEventFeedChatSurface
                        state={chatViewState}
                        onSubmit={() => false}
                        readOnly={true}
                        organizationId={trace.organization_id}
                        emptyState="No events yet"
                        showGeneralOutreachProgressCards={true}
                        showRecommendationTraceProgress={true}
                        isMonitoringOutreach={isMonitoringOutreach}
                        monitoringControls={monitoringControls}
                        monitoringScheduleMetadata={monitoringScheduleMetadata}
                        shiftActionPolicy={shiftActionPolicy}
                        renderMessageFeedback={renderMessageFeedback}
                        renderAssistantMessageActions={
                            renderAssistantMessageActions
                        }
                        focusAgentItemId={focusAgentItemId}
                        onOpenSubagentRun={handleOpenSubagentRun}
                    />
                </IdentifierRedactionProvider>
            </div>
        </div>
    );
}

export function AdminAgentTraceSidebar({
    adminAgentRunId,
    conversationId,
    onAdminAgentRunIdChange,
    onTriageWithAdminAgent,
    pendingPrompt,
    onPendingPromptHandled,
    trace,
    mutate,
}: {
    readonly adminAgentRunId: string | null;
    readonly conversationId: string;
    readonly onAdminAgentRunIdChange: (
        runId: string | null,
    ) => Promise<void> | void;
    readonly onTriageWithAdminAgent: () => void;
    readonly pendingPrompt: PendingAdminAgentPrompt | null;
    readonly onPendingPromptHandled: (
        handledPrompt: PendingAdminAgentPrompt,
    ) => void;
    readonly trace: AgentTraceDetail;
    readonly mutate: () => Promise<unknown>;
}) {
    return (
        <AdminAgentTraceSidebarContent
            key={trace.id ?? ''}
            adminAgentRunId={adminAgentRunId}
            conversationId={conversationId}
            onAdminAgentRunIdChange={onAdminAgentRunIdChange}
            onTriageWithAdminAgent={onTriageWithAdminAgent}
            pendingPrompt={pendingPrompt}
            onPendingPromptHandled={onPendingPromptHandled}
            trace={trace}
            mutate={mutate}
        />
    );
}

function AdminAgentTraceSidebarContent({
    adminAgentRunId,
    conversationId,
    onAdminAgentRunIdChange,
    onTriageWithAdminAgent,
    pendingPrompt,
    onPendingPromptHandled,
    trace,
    mutate,
}: {
    readonly adminAgentRunId: string | null;
    readonly conversationId: string;
    readonly onAdminAgentRunIdChange: (
        runId: string | null,
    ) => Promise<void> | void;
    readonly onTriageWithAdminAgent: () => void;
    readonly pendingPrompt: PendingAdminAgentPrompt | null;
    readonly onPendingPromptHandled: (
        handledPrompt: PendingAdminAgentPrompt,
    ) => void;
    readonly trace: AgentTraceDetail;
    readonly mutate: () => Promise<unknown>;
}) {
    const navigate = useNavigate();
    const { data: user } = useUser({
        reason: 'admin-agent-trace-sidebar-collapse',
    });
    const [isRunningJudge, setIsRunningJudge] = useState(false);
    const [activeTab, setActiveTab] = useState<TraceDiagnosticsTab>('review');
    const [judgeModel, setJudgeModel] = useState(
        DEFAULT_AGENT_TRACE_JUDGE_MODEL,
    );
    const { data: selectedRun, mutate: mutateSelectedRun } =
        useAdminAgentRun(adminAgentRunId);
    const { organizations: adminOrganizations } = useAllAdminOrganizations({
        autoLoadPages: true,
        orgDataOnly: true,
    });
    const defaultAdminOrganization = useMemo(
        () =>
            adminOrganizations.find((organization) =>
                isDefaultAdminAgentOrganization(organization),
            ) ?? null,
        [adminOrganizations],
    );
    const storageKey = collapseStorageKey(user?.id);
    const [collapseState, setCollapseState] =
        useState<TraceSidebarCollapseState>({
            storageKey: null,
            isOpen: true,
        });
    const isOpen =
        collapseState.storageKey === storageKey
            ? collapseState.isOpen
            : storageKey
              ? readTraceSidebarOpen(storageKey)
              : true;

    useEffect(() => {
        setCollapseState({
            storageKey,
            isOpen: storageKey ? readTraceSidebarOpen(storageKey) : true,
        });
    }, [storageKey]);

    const setSidebarOpen = useCallback(
        (nextIsOpen: boolean) => {
            setCollapseState({
                storageKey,
                isOpen: nextIsOpen,
            });
            if (storageKey) {
                writeTraceSidebarOpen(storageKey, nextIsOpen);
            }
        },
        [storageKey],
    );

    const pageContext = useMemo(
        () =>
            assertAdminAgentPageContext(
                resolveCurrentAgentTracePageContext(trace),
            ),
        [trace],
    );
    const adminAgentSession = useAdminAgentChatSession({
        canUseAdminAgent: pageContext !== null && !!defaultAdminOrganization,
        chrome: {
            embedded: true,
            showFullPageChrome: false,
            showHistory: false,
        },
        currentMode: trace.mode,
        persistTierPreference: true,
        initialPageContext: pageContext,
        isSelectedRunArchived: false,
        listOrganizationId: defaultAdminOrganization?.id ?? '',
        mutateRun: mutateSelectedRun,
        navigateOnCreate: false,
        onRunCreated: async (runId) => {
            await onAdminAgentRunIdChange(runId);
        },
        refreshRunLists: () => {},
        selectedOrganizationApprovalName: trace.organization_name,
        selectedOrganizationId: trace.organization_id,
        selectedRun: selectedRun ?? null,
        selectedRunId: adminAgentRunId,
        selectedRunStatus: selectedRun?.status ?? null,
        streamOrganizationId: defaultAdminOrganization?.id ?? '',
    });
    const canSubmitAdminAgentMessage = adminAgentSession.canSendMessage;
    const submitAdminAgentMessage = adminAgentSession.submitMessage;
    const submittedPendingPromptAttemptRef = useRef<number | null>(null);
    const inFlightPendingPromptRef = useRef<{
        readonly conversationId: string;
        readonly prompt: string;
    } | null>(null);
    useEffect(() => {
        if (!pendingPrompt || pendingPrompt.conversationId !== conversationId) {
            submittedPendingPromptAttemptRef.current = null;
            inFlightPendingPromptRef.current = null;
            return;
        }
        const inFlightPrompt = inFlightPendingPromptRef.current;
        if (
            (inFlightPrompt?.conversationId === pendingPrompt.conversationId &&
                inFlightPrompt.prompt === pendingPrompt.prompt) ||
            !canSubmitAdminAgentMessage ||
            submittedPendingPromptAttemptRef.current === pendingPrompt.attempt
        ) {
            return;
        }
        const handledPrompt = pendingPrompt;
        const prompt = handledPrompt.prompt;
        submittedPendingPromptAttemptRef.current = pendingPrompt.attempt;
        inFlightPendingPromptRef.current = {
            conversationId: handledPrompt.conversationId,
            prompt,
        };
        setSidebarOpen(true);
        setActiveTab('admin-agent');
        void submitAdminAgentMessage(prompt, {
            trigger: 'form_submit',
        })
            .then((outcome) => {
                if (outcome === false) {
                    toast.error('Failed to start Admin Agent triage', {
                        persistNotification: false,
                    });
                    return;
                }
                if (outcome === true) {
                    onPendingPromptHandled(handledPrompt);
                }
            })
            .catch((error) => {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to start Admin Agent triage', {
                        persistNotification: false,
                    });
                }
            })
            .finally(() => {
                const currentInFlightPrompt = inFlightPendingPromptRef.current;
                if (
                    currentInFlightPrompt?.conversationId ===
                        handledPrompt.conversationId &&
                    currentInFlightPrompt.prompt === handledPrompt.prompt
                ) {
                    inFlightPendingPromptRef.current = null;
                }
            });
    }, [
        canSubmitAdminAgentMessage,
        conversationId,
        onPendingPromptHandled,
        pendingPrompt,
        setSidebarOpen,
        submitAdminAgentMessage,
    ]);
    const activeAdminAgentRunId = adminAgentSession.allowedSelectedRunId;
    const renderMessageArtifacts = useMemo(
        () => createAdminMessageArtifactRenderer(activeAdminAgentRunId),
        [activeAdminAgentRunId],
    );
    const renderToolArtifacts = useMemo(
        () =>
            createAdminToolArtifactRenderer(activeAdminAgentRunId, {
                suppressMessageArtifacts: true,
            }),
        [activeAdminAgentRunId],
    );
    const chatPanelSession = useMemo(
        () => ({
            ...adminAgentSession,
            renderMessageArtifacts,
            renderToolArtifacts,
        }),
        [adminAgentSession, renderMessageArtifacts, renderToolArtifacts],
    );

    const tierPicker = (
        <AdminAgentTierPicker
            selectedTier={adminAgentSession.selectedTier}
            resolvedTier={adminAgentSession.selectedResolvedTier}
            resolutionReason={adminAgentSession.selectedTierResolutionReason}
            disabled={
                adminAgentSession.isUpdatingTier ||
                adminAgentSession.isCreatingRun
            }
            onTierChange={(tier) => {
                void adminAgentSession.updateSelectedTier(tier);
            }}
        />
    );
    const openFullAdminAgent = () => {
        if (!activeAdminAgentRunId) {
            return;
        }
        void navigate({
            to: '/admin/agent/$runId',
            params: { runId: activeAdminAgentRunId },
        });
    };
    const latestJudgeNote = getLatestAgentTraceJudgeNote(trace);

    const handleTriageFromReview = useCallback(() => {
        setSidebarOpen(true);
        setActiveTab('admin-agent');
        onTriageWithAdminAgent();
    }, [onTriageWithAdminAgent, setSidebarOpen]);

    const handleRunJudge = useCallback(async () => {
        if (isRunningJudge) {
            return;
        }
        setIsRunningJudge(true);
        try {
            const response = await getApiClient().post({
                url: '/admin/agent-traces/{conversation_id}/judge',
                pathParams: {
                    conversation_id: conversationId,
                },
                body: {
                    model: judgeModel,
                },
            });
            await mutate();
            toast.success(
                `LLM judge finished with ${response.labels.length} label${
                    response.labels.length === 1 ? '' : 's'
                }`,
                { persistNotification: false },
            );
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to run LLM judge',
                    { persistNotification: false },
                );
            }
        } finally {
            setIsRunningJudge(false);
        }
    }, [conversationId, isRunningJudge, judgeModel, mutate]);

    if (!isOpen) {
        return (
            <aside className="hidden w-12 shrink-0 self-stretch lg:flex">
                <div className="bg-card border-border flex h-full min-h-0 w-full flex-col items-center rounded-lg border py-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Open Admin Agent sidebar"
                        className="size-8"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <PanelRightOpen className="size-4" />
                    </Button>
                    <div className="text-muted-foreground mt-3 text-xs font-medium [writing-mode:vertical-rl]">
                        Diagnostics
                    </div>
                </div>
            </aside>
        );
    }

    return (
        <aside className="hidden w-[25rem] shrink-0 self-stretch lg:flex">
            <div className="bg-card border-border flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border">
                <div className="border-border flex h-11 shrink-0 items-center gap-2 border-b px-3">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Collapse Admin Agent sidebar"
                        className="size-8"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <PanelRightClose className="size-4" />
                    </Button>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">
                            Trace Diagnostics
                        </div>
                    </div>
                    {tierPicker}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Open in full Admin Agent"
                        disabled={!activeAdminAgentRunId}
                        className="size-8"
                        onClick={openFullAdminAgent}
                    >
                        <ExternalLink className="size-4" />
                    </Button>
                </div>
                <Tabs
                    value={activeTab}
                    onValueChange={(value) => {
                        if (
                            value === 'review' ||
                            value === 'admin-agent' ||
                            value === 'llm-judge'
                        ) {
                            setActiveTab(value);
                        }
                    }}
                    className="flex min-h-0 flex-1 flex-col gap-0"
                >
                    <TabsList className="mx-3 mt-3 grid w-auto shrink-0 grid-cols-3">
                        <TabsTrigger value="review">Review</TabsTrigger>
                        <TabsTrigger value="admin-agent">
                            Admin Agent
                        </TabsTrigger>
                        <TabsTrigger value="llm-judge">LLM Judge</TabsTrigger>
                    </TabsList>
                    <TabsContent
                        value="review"
                        className="mt-0 min-h-0 flex-1 overflow-hidden px-3 pt-3 pb-3"
                    >
                        <AdminAgentTraceReviewPanel
                            conversationId={conversationId}
                            trace={trace}
                            mutate={mutate}
                            onTriageWithAdminAgent={handleTriageFromReview}
                            shortcutsEnabled={isOpen && activeTab === 'review'}
                        />
                    </TabsContent>
                    <TabsContent
                        value="admin-agent"
                        className="mt-0 min-h-0 flex-1 overflow-hidden"
                    >
                        <div className="flex h-full min-h-0 flex-col">
                            <AdminAgentChatPanel
                                session={chatPanelSession}
                                leadingControl={null}
                                placeholder="Ask about this trace…"
                                emptyState="Ask the Admin Agent to investigate this trace."
                                emptyComposerPlacement="bottom"
                                canStopRun={
                                    adminAgentSession.isSelectedRunBusy &&
                                    !!activeAdminAgentRunId
                                }
                                onInspectToolEntry={(entry) =>
                                    adminAgentSession.setInspectedToolEntryId(
                                        getToolEntryInspectionId(entry),
                                    )
                                }
                                renderAssistantMessageActions={() => null}
                            />
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="llm-judge"
                        className={cn(
                            'mt-0 flex-1',
                            AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME,
                        )}
                    >
                        <AgentTraceJudgePanel
                            note={latestJudgeNote}
                            judgeModel={judgeModel}
                            isRunningJudge={isRunningJudge}
                            onJudgeModelChange={setJudgeModel}
                            onRunJudge={() => {
                                void handleRunJudge();
                            }}
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </aside>
    );
}

export const Route = createFileRoute(
    '/_app/admin/agent-traces/$conversationId',
)({
    component: AgentTraceDetailPage,
    head: () => ({ meta: [{ title: 'Agent Trace Detail' }] }),
    validateSearch: agentTraceDetailSearchSchema,
});

function AgentTraceDetailPage() {
    const { conversationId } = Route.useParams();
    const navigate = Route.useNavigate();
    const router = useRouter();
    const search = Route.useSearch();
    const listNav = useTraceListNavigation(conversationId, search);
    // Rows on the Agent Analysis page navigate here with `from_analysis`;
    // the back arrow then returns to that page — via history when possible
    // so its calendar range survives — instead of the trace list.
    const backButton = search.from_analysis ? (
        <Button
            variant="ghost"
            size="sm"
            aria-label="Back to agent analysis"
            onClick={() => {
                if (router.history.canGoBack()) {
                    router.history.back();
                    return;
                }
                void navigate({ to: '/admin/agent-analysis' });
            }}
        >
            <ArrowLeft className="h-4 w-4" />
        </Button>
    ) : search.from_sandbox ? (
        <Link
            to="/admin/sandbox"
            search={{ ...detailSearchToListSearch(search), tab: 'runs' }}
            aria-label="Back to sandbox runs"
        >
            <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4" />
            </Button>
        </Link>
    ) : (
        <Link
            to="/admin/agent-traces"
            search={detailSearchToListSearch(search)}
        >
            <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4" />
            </Button>
        </Link>
    );
    const selectedAdminAgentRunId = getDetailAdminAgentRunIdForTrace(
        search,
        conversationId,
    );
    const { setOrganizationId } = useOrganizationContext();
    // Two-phase load (PHO-14248): the fast summary endpoint (no
    // turn/event payloads) answers first — painting the header and
    // publishing the dock's trace chip with structured facts — and
    // only then does the heavyweight trace detail request start.
    const { data: traceSummary, error: traceSummaryError } =
        useAdminAgentTraceSummary(conversationId);
    // The detail read is gated on summary settlement (data OR error:
    // a 404 summary must still let the detail 404 drive the run-id
    // redirect probe below) so the deep response never blocks or
    // races the initial paint.
    const summarySettled =
        traceSummary !== undefined || traceSummaryError !== undefined;
    const {
        data: trace,
        error: traceError,
        isLoading: isLoadingTraceDetail,
        mutate,
    } = useAdminAgentTrace(summarySettled ? conversationId : null);
    useRouteChipPublisher(
        `/admin/agent-traces/${conversationId}`,
        traceSummary ? buildAgentTraceChip(conversationId, traceSummary) : null,
    );
    const shouldProbeRun = traceError?.status === 404;
    const { data: probedRun, isLoading: isLoadingRunProbe } = useAdminAgentRun(
        shouldProbeRun ? conversationId : null,
    );

    useEffect(() => {
        if (!shouldProbeRun || !probedRun) {
            return;
        }
        void navigate({
            to: '/admin/agent-traces/run/$runId',
            params: { runId: conversationId },
            replace: true,
        });
    }, [conversationId, navigate, probedRun, shouldProbeRun]);

    const { data: modelOptions } = useAgentModelOptions();
    const [systemPrompt, setSystemPrompt] = useState('');
    const [promptInitialized, setPromptInitialized] = useState(false);
    const [model, setModel] = useState<string>('');
    const [rerunning, setRerunning] = useState(false);
    const [syntheticFeatures, setSyntheticFeatures] = useState('');
    const [syntheticPersona, setSyntheticPersona] = useState('');
    const [syntheticScenario, setSyntheticScenario] = useState('');
    const [generatingQuery, setGeneratingQuery] = useState(false);
    const [generatedQuery, setGeneratedQuery] = useState('');
    const [playgroundConversationId, setPlaygroundConversationId] = useState<
        string | null
    >(null);
    const [pendingAdminAgentPrompt, setPendingAdminAgentPrompt] =
        useState<PendingAdminAgentPrompt | null>(null);
    useEffect(() => {
        setPendingAdminAgentPrompt((current) =>
            current?.conversationId === conversationId ? current : null,
        );
    }, [conversationId]);
    const activeConversationIdRef = useRef<string | null>(conversationId);
    useLayoutEffect(() => {
        activeConversationIdRef.current = conversationId;
        return () => {
            if (activeConversationIdRef.current === conversationId) {
                activeConversationIdRef.current = null;
            }
        };
    }, [conversationId]);
    const setSelectedAdminAgentRunId = useCallback(
        async (runId: string | null) => {
            if (activeConversationIdRef.current !== conversationId) {
                return;
            }
            await navigate({
                search: (current) =>
                    detailSearchWithAdminAgentRun(
                        current,
                        conversationId,
                        runId,
                    ),
                replace: true,
            });
        },
        [conversationId, navigate],
    );

    const ownerCandidates = useOwnerCandidates();
    const [isAssigningOwner, setIsAssigningOwner] = useState(false);
    // Reassignment is allowed regardless of review state — ownership is a
    // separate concept from whether the trace has been reviewed.
    const handleAssignOwner = useCallback(
        async (ownerUserId: string | null) => {
            setIsAssigningOwner(true);
            try {
                await updateAgentTraceOwner(conversationId, {
                    owner_user_id: ownerUserId,
                });
                await mutate();
                toast.success('Trace owner updated', {
                    persistNotification: false,
                });
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to update trace owner', {
                        persistNotification: false,
                    });
                }
            } finally {
                setIsAssigningOwner(false);
            }
        },
        [conversationId, mutate],
    );

    // Navigating between traces in place (e.g. via the prev/next arrows) reuses
    // this component, so the Playground prompt must re-seed from the new trace
    // rather than stay pinned to the first one opened. Clear it too so a trace
    // with no system prompt doesn't keep showing the previous one's.
    useEffect(() => {
        setPromptInitialized(false);
        setSystemPrompt('');
    }, [conversationId]);

    useEffect(() => {
        if (trace?.system_prompt && !promptInitialized) {
            setSystemPrompt(trace.system_prompt);
            setPromptInitialized(true);
        }
    }, [trace?.system_prompt, promptInitialized]);

    useEffect(() => {
        setPlaygroundConversationId(trace?.playground_conversation_id ?? null);
    }, [conversationId, trace?.playground_conversation_id]);

    const debugSnapshot = useMemo<PhoebeChatDebugSnapshot | null>(() => {
        if (!trace) {
            return null;
        }
        return buildPhoebeChatDebugSnapshot({
            conversationId,
            conversationTitle: trace.organization_name ?? 'Unknown org',
            phoebeRunId: trace.phoebe_run_id,
            agentGeneration: trace.is_agent_v3 ? 'v3' : 'v2',
            eventReadEnabled: trace.phoebe_run_id !== null,
            connectionStatus: 'idle',
            runState: trace.run_state,
            events: trace.events,
            pendingEvents: trace.pending_events,
        });
    }, [conversationId, trace]);

    // Coordinate polling across tabs so one visible page refreshes the heavy trace.
    useEffect(() => {
        const ownerId = createAgentTracePollingOwnerId();
        const releaseCurrentLease = () => {
            releaseAgentTracePollingLease({
                conversationId,
                ownerId,
            });
        };

        if (!trace?.agent_thinking) {
            releaseCurrentLease();
            return;
        }

        const refreshIfPollingOwner = () => {
            if (document.hidden) {
                releaseCurrentLease();
                return;
            }
            if (
                !tryAcquireAgentTracePollingLease({
                    conversationId,
                    ownerId,
                })
            ) {
                return;
            }
            void mutate();
        };

        refreshIfPollingOwner();
        const interval = window.setInterval(
            refreshIfPollingOwner,
            AGENT_TRACE_DETAIL_POLL_INTERVAL_MS,
        );
        document.addEventListener('visibilitychange', refreshIfPollingOwner);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener(
                'visibilitychange',
                refreshIfPollingOwner,
            );
            releaseCurrentLease();
        };
    }, [conversationId, trace?.agent_thinking, mutate]);

    const handleRerun = useCallback(async () => {
        if (!systemPrompt.trim()) {
            return;
        }
        setRerunning(true);
        try {
            const response = await getApiClient().post({
                url: '/admin/agent-traces/{conversation_id}/rerun',
                pathParams: {
                    conversation_id: conversationId,
                },
                body: {
                    system_prompt: systemPrompt,
                    model: model || undefined,
                },
            });
            setPlaygroundConversationId(response.conversation_id);
            toast.success('Rerun started', { persistNotification: false });
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to start rerun', {
                    persistNotification: false,
                });
            }
        } finally {
            setRerunning(false);
        }
    }, [conversationId, systemPrompt, model]);

    const handleCopySystemPrompt = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(systemPrompt);
            toast.success('Copied system prompt', {
                persistNotification: false,
            });
        } catch {
            toast.error('Failed to copy system prompt', {
                persistNotification: false,
            });
        }
    }, [systemPrompt]);

    const handleGenerateQuery = useCallback(async () => {
        if (
            !syntheticFeatures.trim() ||
            !syntheticPersona.trim() ||
            !syntheticScenario.trim()
        ) {
            toast.error('Fill in all of features, persona, and scenario', {
                persistNotification: false,
            });
            return;
        }
        setGeneratingQuery(true);
        try {
            const response = await getApiClient().post({
                url: '/admin/agent-traces/generate-synthetic-query',
                body: {
                    features: syntheticFeatures.trim(),
                    persona: syntheticPersona.trim(),
                    scenario: syntheticScenario.trim(),
                },
            });
            setGeneratedQuery(response.query);
        } catch (e) {
            if (!isTransparentRateLimitError(e)) {
                toast.error(
                    `Failed to generate query: ${e instanceof Error ? e.message : 'Unknown error'}`,
                    { persistNotification: false },
                );
            }
        } finally {
            setGeneratingQuery(false);
        }
    }, [syntheticFeatures, syntheticPersona, syntheticScenario]);

    const handleSendGeneratedQuery = useCallback(async () => {
        if (!playgroundConversationId || !generatedQuery.trim()) {
            return;
        }
        setRerunning(true);
        try {
            await getApiClient().post({
                url: '/admin/agent-traces/{conversation_id}/messages',
                pathParams: {
                    conversation_id: playgroundConversationId,
                },
                body: {
                    content: generatedQuery.trim(),
                },
            });
            toast.success('Query sent to playground', {
                persistNotification: false,
            });
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to send query', {
                    persistNotification: false,
                });
            }
        } finally {
            setRerunning(false);
        }
    }, [generatedQuery, playgroundConversationId]);
    const handleTriageWithAdminAgent = useCallback(() => {
        if (!trace) {
            return;
        }
        setPendingAdminAgentPrompt((current) => ({
            conversationId,
            prompt: buildAgentTraceTriagePrompt(trace),
            attempt:
                current?.conversationId === conversationId
                    ? current.attempt + 1
                    : 1,
        }));
    }, [conversationId, trace]);

    if (
        !summarySettled ||
        isLoadingTraceDetail ||
        // Covers the render frames after the summary settles but
        // before the just-enabled detail request reports isLoading,
        // so the not-found state can't flash.
        (trace === undefined && traceError === undefined) ||
        (shouldProbeRun && (isLoadingRunProbe || probedRun !== undefined))
    ) {
        // Two-phase initial state, keyed to the SUMMARY: while the
        // summary is in flight the page shows a bare skeleton; once
        // it settles the header paints from `traceSummary` (and the
        // dock chip publishes) while the gated heavyweight detail
        // request runs behind the body skeletons.
        return (
            <DetailTwoPaneShell
                collapsible={false}
                header={
                    <div className="flex shrink-0 items-center gap-3">
                        {backButton}
                        <div className="flex-1">
                            {traceSummary ? (
                                <Fragment>
                                    <h1 className="text-lg font-semibold">
                                        {traceSummary.organization_name ??
                                            'Unknown org'}
                                    </h1>
                                    <div className="text-muted-foreground text-xs">
                                        Created{' '}
                                        {format(
                                            new Date(traceSummary.created_at),
                                            'MMM d, h:mm a',
                                        )}
                                    </div>
                                </Fragment>
                            ) : (
                                <div
                                    className="flex flex-col gap-1.5"
                                    aria-label="Loading trace"
                                >
                                    <Skeleton className="h-6 w-56" />
                                    <Skeleton className="h-3.5 w-36" />
                                </div>
                            )}
                        </div>
                    </div>
                }
                summaryCards={<Skeleton className="h-24 w-full shrink-0" />}
                tabs={<Skeleton className="min-h-[360px] w-full flex-1" />}
            />
        );
    }

    if (!trace) {
        return <DetailTwoPaneShellNotFound message="Trace not found." />;
    }

    const childRunCount = trace.child_runs?.length ?? 0;
    const executionBackendLabel = getAgentTraceExecutionBackendLabel(
        trace.execution_backend,
    );
    const sourceRelationships = getSourceRunRelationships(
        trace.run_relationships ?? [],
    );

    return (
        <DetailTwoPaneShell
            collapsible={false}
            header={
                <div className="flex shrink-0 items-center gap-3">
                    {backButton}
                    <ListNavControls navigation={listNav} />
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg font-semibold">
                                {trace.outreach_id ? (
                                    <Link
                                        to="/outreaches/$callout-id"
                                        params={{
                                            'callout-id': trace.outreach_id,
                                        }}
                                        onClick={() => {
                                            setOrganizationId(
                                                trace.organization_id,
                                            );
                                        }}
                                        className="hover:text-foreground text-muted-foreground underline"
                                    >
                                        {trace.organization_name ??
                                            'Unknown org'}
                                        {` · ${trace.outreach_id}`}
                                    </Link>
                                ) : (
                                    (trace.organization_name ?? 'Unknown org')
                                )}
                            </h1>
                            {trace.agent_thinking && (
                                <Badge variant="outline">
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    Running
                                </Badge>
                            )}
                            {trace.run_type === 'subagent' ? (
                                <Badge variant="secondary">Subagent</Badge>
                            ) : null}
                            {trace.imessage_debug ? (
                                <Badge
                                    variant="secondary"
                                    className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
                                >
                                    {isImessageRouterTrace(trace)
                                        ? 'iMessage'
                                        : 'iMessage subagent'}
                                </Badge>
                            ) : null}
                            {trace.is_agent_v3 ? (
                                <Badge variant="secondary">Agent v3</Badge>
                            ) : null}
                            {trace.is_agent_v3_internal ? (
                                <Badge variant="secondary">
                                    Internal read-only
                                </Badge>
                            ) : null}
                        </div>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                            <span>
                                {trace.organization_name ?? 'Unknown org'}
                            </span>
                            <span>
                                Created{' '}
                                {format(
                                    new Date(trace.created_at),
                                    'MMM d, h:mm a',
                                )}
                            </span>
                            {trace.created_by_user_id ? (
                                <span>
                                    {'by '}
                                    <Link
                                        to="/admin/users/$user-id"
                                        params={{
                                            'user-id': trace.created_by_user_id,
                                        }}
                                        className="text-primary underline"
                                    >
                                        {trace.created_by_user_name ??
                                            trace.created_by_user_email ??
                                            'Unknown user'}
                                        {trace.created_by_user_email &&
                                        trace.created_by_user_name
                                            ? ` (${trace.created_by_user_email})`
                                            : null}
                                    </Link>
                                </span>
                            ) : null}
                            {trace.review.reviewed_at ? (
                                <span>
                                    Reviewed{' '}
                                    {format(
                                        new Date(trace.review.reviewed_at),
                                        'MMM d, h:mm a',
                                    )}
                                </span>
                            ) : null}
                            <BraintrustExecutionLinks
                                executions={trace.executions}
                                runBraintrustUrl={
                                    trace.braintrust_run_url ?? null
                                }
                            />
                            {trace.parent_run ? (
                                <RelatedRunLink
                                    run={trace.parent_run}
                                    label="Parent run"
                                    variant="inline"
                                />
                            ) : null}
                            {childRunCount > 0 ? (
                                <Badge variant="secondary" className="text-xs">
                                    {childRunCount} subagent
                                    {childRunCount === 1 ? '' : 's'}
                                </Badge>
                            ) : null}
                            {trace.outreach_status ? (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        'text-xs',
                                        trace.outreach_status === 'filled' &&
                                            'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
                                        trace.outreach_status ===
                                            'in_progress' &&
                                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300',
                                        (trace.outreach_status === 'unfilled' ||
                                            trace.outreach_status ===
                                                'cancelled') &&
                                            'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
                                    )}
                                >
                                    {trace.outreach_status.replace('_', ' ')}
                                </Badge>
                            ) : null}
                            {trace.suggested_caregiver_count !== null ? (
                                <span>
                                    {trace.suggested_caregiver_count} suggested
                                </span>
                            ) : null}
                            {trace.outreach_units ? (
                                <span>
                                    {trace.outreach_units.single_shift_count}{' '}
                                    single shift
                                    {trace.outreach_units.single_shift_count ===
                                    1
                                        ? ''
                                        : 's'}
                                    {' · '}
                                    {
                                        trace.outreach_units.shift_group_count
                                    }{' '}
                                    group
                                    {trace.outreach_units.shift_group_count ===
                                    1
                                        ? ''
                                        : 's'}
                                </span>
                            ) : null}
                            {trace.urgent_outreach ? (
                                <Badge
                                    variant="secondary"
                                    className="bg-rose-100 text-xs text-rose-800 dark:bg-rose-950/50 dark:text-rose-300"
                                >
                                    Urgent outreach
                                </Badge>
                            ) : null}
                            {trace.auto_drafted === true ? (
                                <Badge variant="secondary" className="text-xs">
                                    Auto-draft
                                </Badge>
                            ) : null}
                            {executionBackendLabel ? (
                                <Badge
                                    variant="secondary"
                                    className="bg-blue-100 text-xs text-blue-800 dark:bg-blue-950/50 dark:text-blue-300"
                                >
                                    {executionBackendLabel}
                                </Badge>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <span className="text-muted-foreground text-xs">
                            Owner
                        </span>
                        <OwnerSelect
                            adminUsers={ownerCandidates}
                            ownerUserId={trace.review.owner_user_id}
                            ownerUserName={trace.review.owner_user_name}
                            disabled={isAssigningOwner}
                            onChange={handleAssignOwner}
                            className="w-44"
                        />
                    </div>
                </div>
            }
            summaryCards={
                <Fragment>
                    <SourceTraceBanner relationships={sourceRelationships} />
                    <RunMetricsCard
                        metrics={trace.run_metrics}
                        organizationId={trace.organization_id}
                        isRunning={trace.agent_thinking}
                        subagentTally={trace.subagent_cost_tally}
                        subagentChildRuns={trace.child_runs}
                        turns={trace.turns}
                        systemPrompt={trace.system_prompt}
                        events={trace.events}
                    />
                </Fragment>
            }
            tabs={
                <AgentTracePrimaryTabs
                    tabs={CONVERSATION_AGENT_TRACE_PRIMARY_TABS}
                >
                    <TabsContent
                        value="rendered-chat"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-card overflow-hidden rounded-xl border',
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <AdminAgentTraceEventChat
                                trace={trace}
                                focusAgentItemId={
                                    search.feedback_item_id ?? null
                                }
                            />
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="model"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-card flex flex-col gap-3 rounded-xl border p-4',
                                AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME,
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            {trace.turns.length === 0 ? (
                                <div className="text-muted-foreground flex flex-1 items-center justify-center py-8 text-center text-sm">
                                    No turns yet.
                                </div>
                            ) : (
                                trace.turns.map((turn) => (
                                    <TurnCard
                                        key={turn.id}
                                        turn={turn}
                                        runStartedAt={
                                            trace.turns[0]?.created_at ??
                                            turn.created_at
                                        }
                                        entityAliases={trace.entity_aliases}
                                        organizationId={trace.organization_id}
                                        mode={trace.mode}
                                    />
                                ))
                            )}
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="graph"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(DETAIL_PANEL_HEIGHT_CLASS, 'min-w-0')}
                        >
                            <AgentTraceAgentGraph trace={trace} />
                        </div>
                    </TabsContent>
                    <TabsContent value="files" className="overflow-auto pt-2">
                        {trace.phoebe_run_id ? (
                            <SandboxFileExplorer
                                key={trace.phoebe_run_id}
                                runId={trace.phoebe_run_id}
                            />
                        ) : (
                            <p className="text-admin-ink-muted p-4 text-sm">
                                No sandbox run is linked to this trace.
                            </p>
                        )}
                    </TabsContent>
                    <TabsContent
                        value="events"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-admin-surface grid overflow-hidden rounded-xl border',
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <div className="min-h-0 overflow-hidden">
                                <AgentTraceTranscript
                                    snapshot={debugSnapshot}
                                />
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="episodes"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'space-y-3 pr-1',
                                AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME,
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <AgentTraceEpisodesPanel
                                trace={trace}
                                organizationTimezone={
                                    trace.organization_timezone
                                }
                            />
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="subagents"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'space-y-3 pr-1',
                                AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME,
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <ImessageSubagentsPanel trace={trace} />
                            <SubagentRunsPanel
                                parentRun={trace.parent_run}
                                childRuns={trace.child_runs ?? []}
                                runRelationships={trace.run_relationships ?? []}
                                runType={trace.run_type}
                            />
                            <OutreachDraftsPanel
                                drafts={trace.outreach_drafts}
                                embedded={true}
                            />
                        </div>
                    </TabsContent>
                    {/* Playground panel */}
                    <TabsContent
                        value="playground"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'space-y-4 pr-1',
                                AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME,
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            {!playgroundConversationId && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-sm">
                                            No playground conversation yet
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="text-muted-foreground text-sm">
                                        Click Rerun below to create a playground
                                        conversation from the current system
                                        prompt.
                                    </CardContent>
                                </Card>
                            )}
                            {playgroundConversationId && (
                                <div
                                    className={cn(
                                        'bg-card overflow-hidden rounded-xl border',
                                        EMBEDDED_CHAT_HEIGHT_CLASS,
                                    )}
                                >
                                    <AdminPhoebeChatWrapper
                                        organizationId={trace.organization_id}
                                        conversationId={
                                            playgroundConversationId
                                        }
                                        conversationSource="admin"
                                        readOnly={false}
                                    />
                                </div>
                            )}
                            {playgroundConversationId && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-sm">
                                            Synthetic User Query
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="flex flex-col gap-4">
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                                    Features
                                                </label>
                                                <Textarea
                                                    value={syntheticFeatures}
                                                    onChange={(e) =>
                                                        setSyntheticFeatures(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="min-h-[100px] text-xs"
                                                    placeholder="e.g. caregiver search, availability check, distance filtering…"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                                    Persona
                                                </label>
                                                <Textarea
                                                    value={syntheticPersona}
                                                    onChange={(e) =>
                                                        setSyntheticPersona(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="min-h-[100px] text-xs"
                                                    placeholder="e.g. busy coordinator with 50 caregivers, new admin learning the system…"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                                    Scenario
                                                </label>
                                                <Textarea
                                                    value={syntheticScenario}
                                                    onChange={(e) =>
                                                        setSyntheticScenario(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="min-h-[100px] text-xs"
                                                    placeholder="e.g. last-minute open shift for evening shift, need bilingual caregiver…"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                onClick={handleGenerateQuery}
                                                disabled={generatingQuery}
                                                variant="secondary"
                                            >
                                                {generatingQuery ? (
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Sparkles className="mr-2 h-4 w-4" />
                                                )}
                                                Generate Query
                                            </Button>
                                        </div>
                                        {generatedQuery && (
                                            <div className="flex flex-col gap-2">
                                                <label className="text-muted-foreground text-xs font-medium">
                                                    Generated Query
                                                </label>
                                                <Textarea
                                                    value={generatedQuery}
                                                    onChange={(e) =>
                                                        setGeneratedQuery(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="min-h-[80px] text-sm"
                                                />
                                                <div>
                                                    <Button
                                                        onClick={
                                                            handleSendGeneratedQuery
                                                        }
                                                        disabled={
                                                            rerunning ||
                                                            !generatedQuery.trim()
                                                        }
                                                        size="sm"
                                                    >
                                                        {rerunning ? (
                                                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <Send className="mr-2 h-3 w-3" />
                                                        )}
                                                        Send as User Turn
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-sm">
                                            System Prompt
                                        </CardTitle>
                                        <div className="flex items-center gap-4">
                                            <Select
                                                value={model}
                                                onValueChange={setModel}
                                            >
                                                <SelectTrigger className="w-[200px]">
                                                    <SelectValue placeholder="Default model" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(modelOptions ?? []).map(
                                                        (option) => (
                                                            <SelectItem
                                                                key={option.id}
                                                                value={
                                                                    option.id
                                                                }
                                                            >
                                                                {option.label}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                variant="secondary"
                                                onClick={() => {
                                                    void handleCopySystemPrompt();
                                                }}
                                                disabled={!systemPrompt.trim()}
                                            >
                                                <Copy className="mr-2 h-4 w-4" />
                                                Copy
                                            </Button>
                                            <Button
                                                onClick={handleRerun}
                                                disabled={
                                                    rerunning ||
                                                    !systemPrompt.trim()
                                                }
                                            >
                                                {rerunning ? (
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                ) : (
                                                    <RotateCcw className="mr-2 h-4 w-4" />
                                                )}
                                                Rerun
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="flex flex-col gap-4">
                                    {trace.skill_versions &&
                                        Object.keys(trace.skill_versions)
                                            .length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span className="text-muted-foreground">
                                                    Prompt versions:
                                                </span>
                                                {Object.entries(
                                                    trace.skill_versions,
                                                ).map(([skill, version]) => (
                                                    <span
                                                        key={skill}
                                                        className="bg-muted text-foreground rounded px-2 py-0.5 font-mono"
                                                    >
                                                        {skill}: {version}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    {trace.prompt_sha256 ? (
                                        <div className="flex flex-wrap items-center gap-2 text-xs">
                                            <span className="text-muted-foreground">
                                                Prompt fingerprint:
                                            </span>
                                            <span className="bg-muted text-foreground rounded px-2 py-0.5 font-mono">
                                                {trace.prompt_sha256.slice(
                                                    0,
                                                    12,
                                                )}
                                            </span>
                                        </div>
                                    ) : null}
                                    {trace.skill_prompt_sha256 &&
                                        Object.keys(trace.skill_prompt_sha256)
                                            .length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span className="text-muted-foreground">
                                                    Skill fingerprints:
                                                </span>
                                                {Object.entries(
                                                    trace.skill_prompt_sha256,
                                                ).map(([skill, sha]) => (
                                                    <span
                                                        key={skill}
                                                        className="bg-muted text-foreground rounded px-2 py-0.5 font-mono"
                                                    >
                                                        {skill}:{' '}
                                                        {sha.slice(0, 12)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    {trace.feature_profile &&
                                        trace.feature_profile.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span className="text-muted-foreground">
                                                    Features on:
                                                </span>
                                                {trace.feature_profile.map(
                                                    (feature) => (
                                                        <span
                                                            key={feature}
                                                            className="bg-muted text-foreground rounded px-2 py-0.5 font-mono"
                                                        >
                                                            {feature}
                                                        </span>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    <Textarea
                                        value={systemPrompt}
                                        onChange={(e) =>
                                            setSystemPrompt(e.target.value)
                                        }
                                        className="min-h-[400px] font-mono text-xs"
                                        placeholder="System prompt…"
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </AgentTracePrimaryTabs>
            }
            sidebar={
                <AdminAgentTraceSidebar
                    adminAgentRunId={selectedAdminAgentRunId}
                    conversationId={conversationId}
                    onAdminAgentRunIdChange={setSelectedAdminAgentRunId}
                    pendingPrompt={
                        pendingAdminAgentPrompt?.conversationId ===
                        conversationId
                            ? pendingAdminAgentPrompt
                            : null
                    }
                    onPendingPromptHandled={(handledPrompt) =>
                        setPendingAdminAgentPrompt((current) =>
                            clearHandledPendingAdminAgentPrompt(
                                current,
                                handledPrompt,
                            ),
                        )
                    }
                    trace={trace}
                    mutate={mutate}
                    onTriageWithAdminAgent={handleTriageWithAdminAgent}
                />
            }
        />
    );
}

type JsonRecord = Record<string, unknown>;
type AgentTraceReviewNote = AgentTraceDetail['review']['notes'][number];
type AgentTraceReviewAnnotation =
    AgentTraceDetail['review']['annotations'][number];
type AgentTraceReviewStatus = AgentTraceDetail['review']['review_status'];
type AgentTraceReviewDisposition = NonNullable<
    AgentTraceDetail['review']['disposition']
>;
type AgentTraceLinearIssue =
    AgentTraceDetail['review']['linked_linear_issues'][number];

const REVIEW_DISPOSITION_LABELS: Record<AgentTraceReviewDisposition, string> = {
    new_issue: 'New issue',
    existing_issue: 'Existing issue',
    false_positive: 'False positive',
    expected_behavior: 'Expected behavior',
    known_issue: 'Known issue',
    could_not_determine: 'Could not determine',
    no_action: 'No action',
};
const REVIEW_DISPOSITION_OPTIONS = Object.entries(
    REVIEW_DISPOSITION_LABELS,
).map(([value, label]) => ({
    value: value as AgentTraceReviewDisposition,
    label,
}));
const DEFAULT_REVIEW_DISPOSITION: AgentTraceReviewDisposition = 'no_action';
const REVIEW_STATUS_BADGE_CLASS_NAMES: Record<AgentTraceReviewStatus, string> =
    {
        needs_review:
            'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
        ticket_filed:
            'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
        false_positive: 'bg-muted text-foreground/80',
        known_issue:
            'bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300',
        reviewed:
            'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    };

function isAgentTraceReviewDisposition(
    value: string,
): value is AgentTraceReviewDisposition {
    return value in REVIEW_DISPOSITION_LABELS;
}

type OutreachDraftEntry = {
    readonly id: string;
    readonly createdAt: string | null;
    readonly output: JsonRecord;
};

function isJsonRecord(value: unknown): value is JsonRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getJsonRecord(value: unknown): JsonRecord | null {
    return isJsonRecord(value) ? value : null;
}

function getJsonRecords(value: unknown): Array<JsonRecord> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isJsonRecord);
}

function getStringValue(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function getNumberValue(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getBooleanValue(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
}

function getStringArray(value: unknown): Array<string> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter((item): item is string => typeof item === 'string');
}

function getLatestAgentTraceJudgeNote(
    trace: AgentTraceDetail,
): AgentTraceReviewNote | null {
    return trace.review.notes
        .filter(
            (note) => note.source_type === AGENT_TRACE_LLM_JUDGE_REVIEW_SOURCE,
        )
        .reduce<AgentTraceReviewNote | null>(
            (latestNote, note) =>
                latestNote === null ||
                note.created_at.localeCompare(latestNote.created_at) > 0
                    ? note
                    : latestNote,
            null,
        );
}

function getReviewStatusBadgeClassName(status: AgentTraceReviewStatus): string {
    return REVIEW_STATUS_BADGE_CLASS_NAMES[status];
}

function buildAgentTraceTriagePrompt(trace: AgentTraceDetail): string {
    const traceUrl =
        typeof window === 'undefined'
            ? `/admin/agent-traces/${trace.id}`
            : window.location.href;
    const linkedIssues = trace.review.linked_linear_issues
        .map((issue) => `${issue.identifier}: ${issue.url}`)
        .join('\n');
    const latestJudgeNote = getLatestAgentTraceJudgeNote(trace);
    return [
        'Investigate the current Agent Trace and draft a human-review triage.',
        '',
        `Trace URL: ${traceUrl}`,
        `Trace ID: ${trace.id}`,
        `Organization: ${trace.organization_name ?? 'Unknown org'}`,
        `Current review status: ${REVIEW_STATUS_LABELS[trace.review.review_status]}`,
        trace.review.final_diagnosis
            ? `Current human diagnosis: ${trace.review.final_diagnosis}`
            : 'Current human diagnosis: none',
        linkedIssues
            ? `Linked Linear issues:\n${linkedIssues}`
            : 'Linked Linear issues: none',
        latestJudgeNote
            ? `Latest LLM judge note:\n${latestJudgeNote.note}`
            : 'Latest LLM judge note: none',
        '',
        'Please summarize the likely root cause, impact, whether this is a duplicate or known issue, the recommended final disposition, and a suggested Linear issue title/body if a ticket is warranted.',
        'Do not create, update, or link a Linear issue until a human explicitly confirms the action.',
    ].join('\n');
}

function isAgentTraceJudgeAnnotation(
    annotation: AgentTraceReviewAnnotation,
): boolean {
    return annotation.source_type === AGENT_TRACE_LLM_JUDGE_REVIEW_SOURCE;
}

function getDraftEntries(drafts: unknown): Array<OutreachDraftEntry> {
    const state = getJsonRecord(drafts);
    const draftsById = getJsonRecord(state?.['drafts']);
    if (!draftsById) {
        return [];
    }

    return Object.entries(draftsById)
        .flatMap(([id, draft]) => {
            const draftRecord = getJsonRecord(draft);
            const output = getJsonRecord(draftRecord?.['output']);
            if (!draftRecord || !output) {
                return [];
            }
            return [
                {
                    id,
                    createdAt: getStringValue(draftRecord['created_at']),
                    output,
                },
            ];
        })
        .sort((a, b) => {
            const createdAtA = a.createdAt ?? '';
            const createdAtB = b.createdAt ?? '';
            return createdAtB.localeCompare(createdAtA);
        });
}

export function getSourceRunRelationships(
    relationships: Array<AgentTraceRunRelationship>,
): Array<AgentTraceRunRelationship> {
    return relationships.filter(
        (relationship) => relationship.direction === 'incoming',
    );
}

export function SourceTraceBanner({
    relationships,
}: {
    readonly relationships: Array<AgentTraceRunRelationship>;
}) {
    if (relationships.length === 0) {
        return null;
    }

    return (
        <div className="bg-card rounded-xl border p-4">
            <div className="flex items-start gap-3">
                <div className="bg-muted text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                    <GitBranch className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-sm font-semibold">
                            Source trace
                            {relationships.length === 1 ? '' : 's'}
                        </h2>
                        <Badge variant="secondary" className="text-xs">
                            {relationships.length}
                        </Badge>
                    </div>
                    <div className="mt-3 grid gap-2">
                        {relationships.map((relationship) => (
                            <div key={relationship.id} className="grid gap-1.5">
                                <div className="text-muted-foreground text-xs font-medium">
                                    {formatRunRelationshipLabel(relationship)}
                                </div>
                                <RelatedRunLink
                                    run={relationship.related_run}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ImessageSubagentRunLink({
    run,
}: {
    readonly run: AgentTraceImessageDebugRun;
}) {
    const content = (
        <div className="flex min-w-0 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
            <div className="min-w-0">
                <div className="truncate font-medium">
                    {imessageSubagentRunLabel(run)}
                </div>
                <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span>{formatRunTypeLabel(run.role)}</span>
                    <span>{run.status.replace(/_/g, ' ')}</span>
                    {run.route_reason ? <span>{run.route_reason}</span> : null}
                    <span>
                        {format(new Date(run.created_at), 'MMM d, h:mm a')}
                    </span>
                    <span className="font-mono">{run.id.slice(0, 8)}</span>
                </div>
            </div>
            {run.conversation_id ? (
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            ) : null}
        </div>
    );

    if (!run.conversation_id) {
        return content;
    }

    return (
        <Link
            to="/admin/agent-traces/$conversationId"
            params={{ conversationId: run.conversation_id }}
            className="hover:bg-muted/50 block rounded-md"
        >
            {content}
        </Link>
    );
}

function ImessageInboundTurnRow({
    turn,
    currentRunId,
    runsById,
}: {
    readonly turn: AgentTraceImessageDebugTurn;
    readonly currentRunId: string | null;
    readonly runsById: ReadonlyMap<string, AgentTraceImessageDebugRun>;
}) {
    const selectedRun = turn.selected_run_id
        ? runsById.get(turn.selected_run_id)
        : undefined;
    return (
        <div className="rounded-md border px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-xs">
                    {format(new Date(turn.created_at), 'MMM d, h:mm a')}
                </span>
                <Badge variant="outline" className="text-xs">
                    {turn.status.replace(/_/g, ' ')}
                </Badge>
                {turn.topic_key ? (
                    <Badge variant="secondary" className="text-xs">
                        {turn.topic_key}
                    </Badge>
                ) : null}
                {turn.selected_run_id === currentRunId &&
                currentRunId !== null ? (
                    <span className="text-muted-foreground text-xs">
                        Handled in this trace
                    </span>
                ) : selectedRun?.conversation_id ? (
                    <Link
                        to="/admin/agent-traces/$conversationId"
                        params={{
                            conversationId: selectedRun.conversation_id,
                        }}
                        className="text-primary text-xs underline"
                    >
                        {selectedRun.role === 'router'
                            ? 'View general chat'
                            : 'View subagent run'}
                    </Link>
                ) : null}
            </div>
            {turn.inbound_text ? (
                <div className="mt-1 truncate" title={turn.inbound_text}>
                    {turn.inbound_text}
                </div>
            ) : null}
        </div>
    );
}

function ImessageSubagentsPanel({
    trace,
}: {
    readonly trace: AgentTraceDetail;
}) {
    const debug = trace.imessage_debug;
    if (!debug) {
        return null;
    }
    const routerRun = debug.router_run;
    const runsById = new Map(
        [...(routerRun ? [routerRun] : []), ...debug.topic_runs].map((run) => [
            run.id,
            run,
        ]),
    );
    return (
        <div className="bg-card rounded-xl border p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold">iMessage subagents</h2>
                <Badge variant="secondary" className="text-xs">
                    {debug.topic_runs.length}
                </Badge>
            </div>
            <div className="grid gap-4">
                {debug.topic_runs.length > 0 ? (
                    <section>
                        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
                            Subagent runs
                        </h3>
                        <div className="grid gap-2">
                            {debug.topic_runs.map((run) => (
                                <ImessageSubagentRunLink
                                    key={run.id}
                                    run={run}
                                />
                            ))}
                        </div>
                    </section>
                ) : (
                    <div className="text-muted-foreground text-sm">
                        No subagent runs yet — every inbound message was handled
                        in the general chat.
                    </div>
                )}
                {debug.turns.length > 0 ? (
                    <section>
                        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
                            Inbound messages
                        </h3>
                        <div className="grid gap-2">
                            {debug.turns.map((turn) => (
                                <ImessageInboundTurnRow
                                    key={turn.id}
                                    turn={turn}
                                    currentRunId={trace.phoebe_run_id}
                                    runsById={runsById}
                                />
                            ))}
                        </div>
                    </section>
                ) : null}
            </div>
        </div>
    );
}

function OutreachDraftsPanel({
    drafts,
    embedded = false,
}: {
    readonly drafts: unknown;
    readonly embedded?: boolean;
}) {
    const draftEntries = useMemo(() => getDraftEntries(drafts), [drafts]);
    const state = getJsonRecord(drafts);
    const latestDraftId = getStringValue(state?.['latest_draft_id']);
    const latestDraft =
        draftEntries.find((draft) => draft.id === latestDraftId) ??
        draftEntries[0] ??
        null;

    if (!latestDraft) {
        return (
            <div
                className={cn(
                    'bg-card flex items-center justify-center rounded-xl border py-8',
                    !embedded && DETAIL_PANEL_HEIGHT_CLASS,
                )}
            >
                <div className="text-muted-foreground text-sm">
                    No persisted subagent draft artifacts.
                </div>
            </div>
        );
    }

    const summary = getJsonRecord(latestDraft.output['summary']);
    const statusCounts = getJsonRecord(summary?.['subagent_status_counts']);
    const units = getJsonRecords(latestDraft.output['unit_drafts']);
    const suggestions = getJsonRecords(latestDraft.output['suggestions']);
    const unresolvedUnits = units.filter((unit) =>
        getStringValue(unit['unresolved_reason']),
    );

    return (
        <div
            className={cn(
                'bg-card flex flex-col gap-4 rounded-xl border p-4',
                embedded ? 'overflow-visible' : 'overflow-y-auto',
                !embedded && DETAIL_PANEL_HEIGHT_CLASS,
            )}
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-sm font-semibold">
                            {latestDraft.id}
                        </h2>
                        {latestDraft.id === latestDraftId ? (
                            <Badge variant="secondary">Latest</Badge>
                        ) : null}
                    </div>
                    <div className="text-muted-foreground mt-1 text-xs">
                        {latestDraft.createdAt
                            ? format(
                                  new Date(latestDraft.createdAt),
                                  'MMM d, h:mm a',
                              )
                            : 'Unknown creation time'}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <DraftMetricBadge
                        label="Suggestions"
                        value={suggestions.length}
                    />
                    <DraftMetricBadge label="Units" value={units.length} />
                    <DraftMetricBadge
                        label="Unresolved"
                        value={unresolvedUnits.length}
                    />
                    <DraftMetricBadge
                        label="Fallbacks"
                        value={summary?.['subagent_fallback_count']}
                    />
                    <DraftMetricBadge
                        label="Timeouts"
                        value={summary?.['subagent_timeout_count']}
                    />
                </div>
            </div>
            {statusCounts ? (
                <div className="flex flex-wrap gap-2">
                    {Object.entries(statusCounts).map(([status, count]) => (
                        <Badge key={status} variant="outline">
                            <span>{status}</span>
                            <span className="ml-1 font-mono">
                                {String(count)}
                            </span>
                        </Badge>
                    ))}
                </div>
            ) : null}
            <div className="grid gap-3">
                {units.map((unit, index) => (
                    <OutreachDraftUnitPanel
                        key={`${latestDraft.id}-${index}`}
                        unit={unit}
                        index={index}
                    />
                ))}
            </div>
            <JsonCollapsible
                label="Full Subagent JSON"
                value={latestDraft.output}
            />
        </div>
    );
}

function DraftMetricBadge({
    label,
    value,
}: {
    readonly label: string;
    readonly value: unknown;
}) {
    const numberValue = getNumberValue(value);
    if (numberValue === null) {
        return null;
    }
    return (
        <Badge variant="outline">
            <span>{label}</span>
            <span className="ml-1 font-mono">{numberValue}</span>
        </Badge>
    );
}

function OutreachDraftUnitPanel({
    unit,
    index,
}: {
    readonly unit: JsonRecord;
    readonly index: number;
}) {
    const suggestions = getJsonRecords(unit['suggestions']);
    const unitContext = getJsonRecord(unit['unit_context']);
    const shifts = getJsonRecords(unitContext?.['shifts']);
    const status = getStringValue(unit['subagent_status']);
    const unresolvedReason = getStringValue(unit['unresolved_reason']);
    const unitId = getStringValue(unit['unit_id']);

    return (
        <div className="rounded-lg border p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">
                            Unit {index + 1}
                        </span>
                        {status ? (
                            <Badge variant="secondary">{status}</Badge>
                        ) : null}
                        {unresolvedReason ? (
                            <Badge variant="destructive">Unresolved</Badge>
                        ) : null}
                    </div>
                    <div className="text-muted-foreground mt-1 text-xs">
                        {getStringValue(unit['unit_type']) ?? 'unit'}
                        {unitId ? ` · ${unitId}` : ''}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <DraftMetricBadge
                        label="Candidates"
                        value={unit['recommendation_count']}
                    />
                    <DraftMetricBadge
                        label="Selected"
                        value={suggestions.length}
                    />
                    <DraftMetricBadge
                        label="Parsed"
                        value={unit['subagent_parsed_suggestion_count']}
                    />
                </div>
            </div>
            {unresolvedReason ? (
                <div className="text-muted-foreground mt-3 text-xs">
                    {unresolvedReason}
                </div>
            ) : null}
            {shifts.length > 0 ? (
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                    {shifts.slice(0, 4).map((shift, shiftIndex) => (
                        <div
                            key={shiftIndex}
                            className="bg-muted/60 rounded-md px-3 py-2 text-xs"
                        >
                            <div className="font-medium">
                                {getStringValue(shift['patient_name']) ??
                                    'Unknown client'}
                            </div>
                            <div className="text-muted-foreground mt-1">
                                {getStringValue(shift['start_time']) ??
                                    'Unknown start'}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
            {suggestions.length > 0 ? (
                <div className="mt-3 grid gap-2">
                    {suggestions
                        .slice(0, 6)
                        .map((suggestion, suggestionIndex) => (
                            <div
                                key={suggestionIndex}
                                className="rounded-md border border-dashed px-3 py-2"
                            >
                                <div className="flex items-center justify-between gap-2 text-xs">
                                    <span className="font-medium">
                                        {getStringValue(
                                            suggestion['caregiver_name'],
                                        ) ??
                                            getStringValue(
                                                suggestion['caregiver_id'],
                                            ) ??
                                            'Unknown caregiver'}
                                    </span>
                                    <DraftMetricBadge
                                        label="Rank"
                                        value={suggestion['rank']}
                                    />
                                </div>
                                {getStringValue(suggestion['reasoning']) ? (
                                    <div className="text-muted-foreground mt-1 text-xs">
                                        {getStringValue(
                                            suggestion['reasoning'],
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                </div>
            ) : null}
            <JsonCollapsible label="Unit JSON" value={unit} />
        </div>
    );
}

function JsonCollapsible({
    label,
    value,
}: {
    readonly label: string;
    readonly value: unknown;
}) {
    const [open, setOpen] = useState(false);

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild={true}>
                <button
                    type="button"
                    className="text-muted-foreground mt-3 flex items-center gap-1 text-xs"
                >
                    {open ? (
                        <ChevronDown className="h-3 w-3" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    )}
                    {label}
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <pre className="bg-muted mt-2 max-h-[360px] overflow-auto rounded p-2 text-[11px]">
                    {JSON.stringify(value, null, 2)}
                </pre>
            </CollapsibleContent>
        </Collapsible>
    );
}

function formatJudgeMetadataLabel(value: string): string {
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatJudgeEvidence(evidenceItem: JsonRecord): string {
    // The judge sometimes emits sequence/index instead of the schema's
    // turn_sequence/item_index; metadata stores its output verbatim.
    const turnSequence =
        getNumberValue(evidenceItem['turn_sequence']) ??
        getNumberValue(evidenceItem['sequence']);
    const itemIndex =
        getNumberValue(evidenceItem['item_index']) ??
        getNumberValue(evidenceItem['index']);
    const quote = getStringValue(evidenceItem['quote']) ?? '';
    if (turnSequence === null) {
        return quote;
    }
    const location =
        itemIndex === null
            ? `Turn ${turnSequence}`
            : `Turn ${turnSequence}.${itemIndex}`;
    return quote ? `${location}: ${quote}` : location;
}

function JudgeCollapsibleSection({
    label,
    badge,
    defaultOpen,
    children,
}: {
    readonly label: string;
    readonly badge?: ReactNode;
    readonly defaultOpen: boolean;
    readonly children: ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <div className="rounded-md border px-3 py-2 text-xs">
                <CollapsibleTrigger asChild={true}>
                    <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2"
                    >
                        <span className="flex items-center gap-1 font-medium">
                            {open ? (
                                <ChevronDown className="h-3 w-3 shrink-0" />
                            ) : (
                                <ChevronRight className="h-3 w-3 shrink-0" />
                            )}
                            {label}
                        </span>
                        {badge}
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent>{children}</CollapsibleContent>
            </div>
        </Collapsible>
    );
}

export function AgentTraceJudgePanel({
    note,
    judgeModel,
    isRunningJudge,
    onJudgeModelChange,
    onRunJudge,
}: {
    readonly note: AgentTraceReviewNote | null;
    readonly judgeModel: string;
    readonly isRunningJudge: boolean;
    readonly onJudgeModelChange: (model: string) => void;
    readonly onRunJudge: () => void;
}) {
    const metadata = getJsonRecord(note?.note_metadata);
    const labels = getStringArray(metadata?.['labels']);
    const failure = getJsonRecord(metadata?.['failure']);
    const checks = getJsonRecord(metadata?.['checks']);
    const usage = getJsonRecord(metadata?.['usage']);
    const deterministicSignals = getJsonRecord(
        metadata?.['deterministic_signals'],
    );
    const quality = getJsonRecord(metadata?.['quality']);
    const llmQuality = getJsonRecord(metadata?.['llm_quality']);
    const score = getJsonRecord(metadata?.['score']);
    const cancellation = getJsonRecord(metadata?.['cancellation']);
    const judgeConversationId = getStringValue(
        metadata?.['judge_conversation_id'],
    );
    const qualityGrade = quality?.['grade'];
    const qualityExplanation = getStringValue(quality?.['explanation']);
    const llmQualityGrade = llmQuality?.['grade'];
    const llmQualityExplanation = getStringValue(llmQuality?.['explanation']);
    const scoreValue = getNumberValue(score?.['value']);
    const scoreGrade = getStringValue(score?.['grade']);
    const scoreVersion = getNumberValue(score?.['version']);
    const scoreInputs = getJsonRecord(score?.['inputs']);
    const scoreInputResources = getJsonRecord(scoreInputs?.['resources']);
    const scoreInputCostUsd = getStringValue(
        scoreInputResources?.['source_estimated_cost_usd'],
    );
    const scoreInputRows = scoreInputResources
        ? [
              {
                  label: 'Source tokens',
                  value: getNumberValue(
                      scoreInputResources['source_total_tokens'],
                  )?.toLocaleString(),
              },
              {
                  label: 'Thinking tokens',
                  value: getNumberValue(
                      scoreInputResources['source_thinking_tokens'],
                  )?.toLocaleString(),
              },
              {
                  label: 'Max turn tokens',
                  value: getNumberValue(
                      scoreInputResources['source_max_total_tokens'],
                  )?.toLocaleString(),
              },
              {
                  label: 'Completed runtime',
                  value:
                      getNumberValue(
                          scoreInputResources['source_active_seconds'],
                      ) !== null
                          ? `${getNumberValue(
                                scoreInputResources['source_active_seconds'],
                            )}s`
                          : undefined,
              },
              {
                  label: 'Source cost',
                  value: scoreInputCostUsd ? `$${scoreInputCostUsd}` : null,
              },
              {
                  label: 'Compactions',
                  value: getNumberValue(
                      scoreInputResources['source_compaction_item_count'],
                  )?.toLocaleString(),
              },
              {
                  label: 'Judge input compacted',
                  value:
                      getBooleanValue(
                          scoreInputResources['judge_input_compacted'],
                      ) === true
                          ? 'Yes'
                          : 'No',
              },
          ].filter(
              (
                  row,
              ): row is { readonly label: string; readonly value: string } =>
                  typeof row.value === 'string' && row.value.length > 0,
          )
        : [];
    const scoreFactors = getJsonRecords(score?.['factors']);
    const cancellationOccurred =
        getBooleanValue(cancellation?.['occurred']) === true;
    const cancellationReason = getStringValue(
        cancellation?.['reason_category'],
    );
    const cancellationExplanation = getStringValue(
        cancellation?.['explanation'],
    );
    const wentWrong = getBooleanValue(failure?.['went_wrong']);
    const severity = getStringValue(failure?.['severity']);
    const model = getStringValue(metadata?.['model']);
    const judgedAt = getStringValue(metadata?.['judged_at']);
    const estimatedCostUsd = getNumberValue(metadata?.['estimated_cost_usd']);
    const checkEntries = checks
        ? Object.entries(checks).flatMap(([key, value]) => {
              const check = getJsonRecord(value);
              if (!check) {
                  return [];
              }
              return [
                  {
                      key,
                      value: getBooleanValue(check['value']) ?? false,
                      explanation: getStringValue(check['explanation']),
                      evidence: getJsonRecords(check['evidence']),
                  },
              ];
          })
        : [];

    return (
        <section className="border-border flex shrink-0 flex-col gap-3 border-b p-3">
            <div className="flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <Sparkles className="h-4 w-4" />
                    LLM Judge
                </h2>
            </div>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <Select
                        value={judgeModel}
                        onValueChange={onJudgeModelChange}
                        disabled={isRunningJudge}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {AGENT_TRACE_JUDGE_MODEL_OPTIONS.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {`${option.label} (${option.provider})`}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button onClick={onRunJudge} disabled={isRunningJudge}>
                        {isRunningJudge ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Sparkles className="mr-2 h-4 w-4" />
                        )}
                        {note ? 'Rerun Judge' : 'Run Judge'}
                    </Button>
                </div>
                {isRunningJudge ? (
                    <div
                        aria-live="polite"
                        className="bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    >
                        Analyzing this trace with{' '}
                        {getAgentTraceJudgeModelLabel(judgeModel)}.
                    </div>
                ) : null}
                {!note ? (
                    <div className="text-muted-foreground text-sm">
                        No LLM judge output yet.
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap gap-2">
                            {wentWrong === true ? (
                                <Badge variant="destructive">
                                    <AlertTriangle className="mr-1 h-3 w-3" />
                                    Went wrong
                                </Badge>
                            ) : wentWrong === false ? (
                                <Badge variant="secondary">
                                    <CheckCircle2 className="mr-1 h-3 w-3" />
                                    Fine
                                </Badge>
                            ) : null}
                            {severity ? (
                                <Badge variant="outline">
                                    {formatJudgeMetadataLabel(severity)}
                                </Badge>
                            ) : null}
                            {isAgentTraceJudgeQualityGrade(qualityGrade) ? (
                                <Badge
                                    variant={getAgentTraceJudgeQualityBadgeVariant(
                                        qualityGrade,
                                    )}
                                >
                                    {`Quality: ${qualityGrade}`}
                                </Badge>
                            ) : null}
                            {cancellationOccurred ? (
                                <Badge variant="destructive">Canceled</Badge>
                            ) : null}
                            {labels
                                // Quality and cancellation already have
                                // dedicated badges above.
                                .filter(
                                    (label) =>
                                        !label.startsWith('quality-') &&
                                        label !== 'canceled' &&
                                        !label.startsWith('canceled-'),
                                )
                                .map((label) => (
                                    <Badge key={label} variant="outline">
                                        {label}
                                    </Badge>
                                ))}
                        </div>
                        <div>
                            <div className="text-muted-foreground mb-1 text-xs font-medium">
                                Summary
                            </div>
                            <div className="rounded-md border px-3 py-2 text-sm whitespace-pre-wrap">
                                {note.note}
                            </div>
                        </div>
                        <div className="text-muted-foreground flex flex-col gap-1 text-xs">
                            {model ? (
                                <span>
                                    Model {getAgentTraceJudgeModelLabel(model)}
                                </span>
                            ) : null}
                            {estimatedCostUsd !== null ? (
                                <span>
                                    Judge cost ${estimatedCostUsd.toFixed(6)}
                                </span>
                            ) : null}
                            {judgedAt ? (
                                <span>
                                    Judged{' '}
                                    {format(
                                        new Date(judgedAt),
                                        'MMM d, h:mm a',
                                    )}
                                </span>
                            ) : (
                                <span>
                                    Saved{' '}
                                    {format(
                                        new Date(note.created_at),
                                        'MMM d, h:mm a',
                                    )}
                                </span>
                            )}
                            {judgeConversationId ? (
                                <Link
                                    to="/admin/agent-traces/$conversationId"
                                    params={{
                                        conversationId: judgeConversationId,
                                    }}
                                    className="text-primary flex items-center gap-1 underline"
                                >
                                    View judge trace
                                    <ChevronRight className="h-3 w-3" />
                                </Link>
                            ) : null}
                        </div>
                        {getStringValue(failure?.['what_happened']) ? (
                            <JudgeCollapsibleSection
                                label="What happened"
                                defaultOpen={true}
                            >
                                <div className="mt-1 text-sm">
                                    {getStringValue(failure?.['what_happened'])}
                                </div>
                            </JudgeCollapsibleSection>
                        ) : null}
                        {getStringValue(failure?.['product_gap']) ? (
                            <JudgeCollapsibleSection
                                label="Product gap"
                                defaultOpen={true}
                            >
                                <div className="mt-1 text-sm">
                                    {getStringValue(failure?.['product_gap'])}
                                </div>
                            </JudgeCollapsibleSection>
                        ) : null}
                        {qualityExplanation ? (
                            <JudgeCollapsibleSection
                                label="Final quality"
                                badge={
                                    isAgentTraceJudgeQualityGrade(
                                        qualityGrade,
                                    ) ? (
                                        <Badge
                                            variant={getAgentTraceJudgeQualityBadgeVariant(
                                                qualityGrade,
                                            )}
                                        >
                                            {formatJudgeMetadataLabel(
                                                qualityGrade,
                                            )}
                                        </Badge>
                                    ) : undefined
                                }
                                defaultOpen={
                                    qualityGrade === 'urgent' ||
                                    qualityGrade === 'bad'
                                }
                            >
                                <div className="mt-1 text-sm">
                                    {qualityExplanation}
                                </div>
                            </JudgeCollapsibleSection>
                        ) : null}
                        {score ? (
                            <JudgeCollapsibleSection
                                label="Risk score"
                                badge={
                                    scoreValue !== null ? (
                                        <Badge
                                            variant={
                                                scoreGrade === 'bad' ||
                                                scoreGrade === 'urgent'
                                                    ? 'destructive'
                                                    : 'secondary'
                                            }
                                        >
                                            {scoreValue}
                                        </Badge>
                                    ) : undefined
                                }
                                defaultOpen={
                                    scoreGrade === 'urgent' ||
                                    scoreGrade === 'bad'
                                }
                            >
                                {scoreValue !== null ? (
                                    <div className="mt-1 text-sm">
                                        {`Risk score ${scoreValue}`}
                                        {scoreGrade
                                            ? ` · ${formatJudgeMetadataLabel(
                                                  scoreGrade,
                                              )}`
                                            : ''}
                                    </div>
                                ) : null}
                                {scoreVersion !== null ? (
                                    <div className="text-muted-foreground mt-1 text-xs">
                                        {`Formula v${scoreVersion}`}
                                    </div>
                                ) : null}
                                {scoreInputRows.length > 0 ? (
                                    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                                        {scoreInputRows.map((row) => (
                                            <div key={row.label}>
                                                <dt className="text-muted-foreground">
                                                    {row.label}
                                                </dt>
                                                <dd className="font-mono text-[11px]">
                                                    {row.value}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                ) : null}
                                {scoreFactors.length > 0 ? (
                                    <ul className="text-muted-foreground mt-2 list-disc space-y-1 pl-4 text-sm">
                                        {scoreFactors.map((factor, index) => {
                                            const factorKey = getStringValue(
                                                factor['key'],
                                            );
                                            const factorPoints = getNumberValue(
                                                factor['points'],
                                            );
                                            const factorExplanation =
                                                getStringValue(
                                                    factor['explanation'],
                                                );
                                            return (
                                                <li key={index}>
                                                    <span>
                                                        {factorKey
                                                            ? formatJudgeMetadataLabel(
                                                                  factorKey,
                                                              )
                                                            : 'Factor'}
                                                    </span>
                                                    {factorPoints !== null ? (
                                                        <span>
                                                            {` (${factorPoints > 0 ? '+' : ''}${factorPoints})`}
                                                        </span>
                                                    ) : null}
                                                    {factorExplanation ? (
                                                        <span>
                                                            {`: ${factorExplanation}`}
                                                        </span>
                                                    ) : null}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null}
                            </JudgeCollapsibleSection>
                        ) : null}
                        {llmQualityExplanation ? (
                            <JudgeCollapsibleSection
                                label="LLM quality"
                                badge={
                                    isAgentTraceJudgeQualityGrade(
                                        llmQualityGrade,
                                    ) ? (
                                        <Badge
                                            variant={getAgentTraceJudgeQualityBadgeVariant(
                                                llmQualityGrade,
                                            )}
                                        >
                                            {formatJudgeMetadataLabel(
                                                llmQualityGrade,
                                            )}
                                        </Badge>
                                    ) : undefined
                                }
                                defaultOpen={llmQualityGrade !== qualityGrade}
                            >
                                <div className="mt-1 text-sm">
                                    {llmQualityExplanation}
                                </div>
                            </JudgeCollapsibleSection>
                        ) : null}
                        {cancellationOccurred ? (
                            <JudgeCollapsibleSection
                                label="Cancellation"
                                badge={
                                    cancellationReason ? (
                                        <Badge variant="destructive">
                                            {formatJudgeMetadataLabel(
                                                cancellationReason,
                                            )}
                                        </Badge>
                                    ) : undefined
                                }
                                defaultOpen={true}
                            >
                                <div className="mt-1 text-sm">
                                    {cancellationExplanation}
                                </div>
                            </JudgeCollapsibleSection>
                        ) : null}
                        {checkEntries.length > 0 ? (
                            <div className="grid gap-2">
                                <div className="text-muted-foreground text-xs font-medium">
                                    Checks
                                </div>
                                {checkEntries.map((check) => (
                                    <JudgeCollapsibleSection
                                        // Reset the open state when a rerun
                                        // flips a check's value.
                                        key={`${check.key}:${check.value}`}
                                        label={formatJudgeMetadataLabel(
                                            check.key,
                                        )}
                                        badge={
                                            <Badge
                                                variant={
                                                    check.value
                                                        ? 'destructive'
                                                        : 'outline'
                                                }
                                            >
                                                {check.value ? 'Yes' : 'No'}
                                            </Badge>
                                        }
                                        defaultOpen={check.value}
                                    >
                                        {check.explanation ? (
                                            <div className="text-muted-foreground mt-1">
                                                {check.explanation}
                                            </div>
                                        ) : null}
                                        {check.evidence.length > 0 ? (
                                            <ul className="text-muted-foreground mt-1 list-disc pl-4">
                                                {check.evidence.map(
                                                    (evidenceItem, index) => (
                                                        <li key={index}>
                                                            {formatJudgeEvidence(
                                                                evidenceItem,
                                                            )}
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        ) : null}
                                    </JudgeCollapsibleSection>
                                ))}
                            </div>
                        ) : null}
                        {usage || deterministicSignals ? (
                            <div className="grid gap-2">
                                {usage ? (
                                    <JsonCollapsible
                                        label="Usage JSON"
                                        value={usage}
                                    />
                                ) : null}
                                {deterministicSignals ? (
                                    <JsonCollapsible
                                        label="Signals JSON"
                                        value={deterministicSignals}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </section>
    );
}

export function AdminAgentTraceReviewPanel({
    conversationId,
    trace,
    mutate,
    onTriageWithAdminAgent,
    shortcutsEnabled,
}: {
    readonly conversationId: string;
    readonly trace: AgentTraceDetail;
    readonly mutate: () => Promise<unknown>;
    readonly onTriageWithAdminAgent: () => void;
    // Whether this panel is the visible sidebar tab; gates its "l" shortcut so
    // it doesn't fire while another tab (or a collapsed sidebar) is showing.
    readonly shortcutsEnabled: boolean;
}) {
    const [noteDraft, setNoteDraft] = useState('');
    const [annotationPickerOpen, setAnnotationPickerOpen] = useState(false);
    const [disposition, setDisposition] = useState<AgentTraceReviewDisposition>(
        trace.review.disposition ?? DEFAULT_REVIEW_DISPOSITION,
    );
    const [finalDiagnosisDraft, setFinalDiagnosisDraft] = useState(
        trace.review.final_diagnosis ?? '',
    );
    const firstLinkedLinearIssue = trace.review.linked_linear_issues[0] ?? null;
    const [linearIdentifierDraft, setLinearIdentifierDraft] = useState(
        firstLinkedLinearIssue?.identifier ?? '',
    );
    const [linearUrlDraft, setLinearUrlDraft] = useState(
        firstLinkedLinearIssue?.url ?? '',
    );
    const [isCreatingNote, setIsCreatingNote] = useState(false);
    const [isSavingReview, setIsSavingReview] = useState(false);
    const [deletingAnnotationIds, setDeletingAnnotationIds] = useState<
        ReadonlySet<string>
    >(() => new Set());
    // Labels with an add/remove request in flight, so a rapid second toggle of
    // the same label doesn't fire a duplicate request before mutate() refreshes.
    const [pendingAnnotationLabels, setPendingAnnotationLabels] = useState<
        ReadonlySet<string>
    >(() => new Set());
    const { data: annotationGroupsData, isLoading: isAnnotationGroupsLoading } =
        useAdminAgentTraceAnnotationGroups({
            page: 1,
            per_page: 50,
        });

    useEffect(() => {
        setDisposition(trace.review.disposition ?? DEFAULT_REVIEW_DISPOSITION);
        setFinalDiagnosisDraft(trace.review.final_diagnosis ?? '');
    }, [
        conversationId,
        trace.review.disposition,
        trace.review.final_diagnosis,
    ]);

    useEffect(() => {
        setLinearIdentifierDraft(firstLinkedLinearIssue?.identifier ?? '');
        setLinearUrlDraft(firstLinkedLinearIssue?.url ?? '');
    }, [
        conversationId,
        firstLinkedLinearIssue?.identifier,
        firstLinkedLinearIssue?.url,
    ]);

    const saveReview = useCallback(
        async ({
            dispositionOverride,
            includeLinearIssue,
        }: {
            readonly dispositionOverride?: AgentTraceReviewDisposition;
            readonly includeLinearIssue: boolean;
        }) => {
            const nextDisposition = dispositionOverride ?? disposition;
            const finalDiagnosis = finalDiagnosisDraft.trim() || null;
            const linearIdentifier = linearIdentifierDraft.trim();
            const linearUrl = linearUrlDraft.trim();
            const linkedLinearIssue =
                includeLinearIssue &&
                (linearIdentifier.length > 0 || linearUrl.length > 0)
                    ? {
                          identifier: linearIdentifier || null,
                          url: linearUrl || null,
                      }
                    : null;
            if (includeLinearIssue && linkedLinearIssue === null) {
                toast.error('Enter a Linear issue ID or URL', {
                    persistNotification: false,
                });
                return;
            }

            setIsSavingReview(true);
            try {
                const response = await getApiClient().put({
                    url: '/admin/agent-traces/{conversation_id}/review',
                    pathParams: {
                        conversation_id: conversationId,
                    },
                    body: {
                        disposition: nextDisposition,
                        final_diagnosis: finalDiagnosis,
                        linked_linear_issue: linkedLinearIssue ?? undefined,
                    },
                });
                setDisposition(response.disposition ?? nextDisposition);
                setFinalDiagnosisDraft(response.final_diagnosis ?? '');
                const firstIssue = response.linked_linear_issues[0] ?? null;
                setLinearIdentifierDraft(firstIssue?.identifier ?? '');
                setLinearUrlDraft(firstIssue?.url ?? '');
                await mutate();
                toast.success(
                    includeLinearIssue
                        ? 'Linear issue linked'
                        : 'Review status updated',
                    { persistNotification: false },
                );
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error(
                        error instanceof Error
                            ? error.message
                            : 'Failed to update review',
                        { persistNotification: false },
                    );
                }
            } finally {
                setIsSavingReview(false);
            }
        },
        [
            conversationId,
            disposition,
            finalDiagnosisDraft,
            linearIdentifierDraft,
            linearUrlDraft,
            mutate,
        ],
    );

    const handleMarkReviewed = useCallback(() => {
        void saveReview({
            includeLinearIssue: false,
        });
    }, [saveReview]);

    const handleLinkLinearIssue = useCallback(() => {
        void saveReview({
            includeLinearIssue: true,
        });
    }, [saveReview]);

    const handleCreateNote = useCallback(async () => {
        if (!noteDraft.trim()) {
            return;
        }
        setIsCreatingNote(true);
        try {
            await getApiClient().post({
                url: '/admin/agent-traces/{conversation_id}/review-notes',
                pathParams: {
                    conversation_id: conversationId,
                },
                body: {
                    note: noteDraft.trim(),
                },
            });
            setNoteDraft('');
            await mutate();
            toast.success('Review note added', { persistNotification: false });
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to add review note', {
                    persistNotification: false,
                });
            }
        } finally {
            setIsCreatingNote(false);
        }
    }, [conversationId, mutate, noteDraft]);

    const handleSelectAnnotationLabel = useCallback(
        async (label: string) => {
            const trimmedLabel = label.trim();
            if (!trimmedLabel) {
                return;
            }
            try {
                await getApiClient().post({
                    url: '/admin/agent-traces/{conversation_id}/review-annotations',
                    pathParams: {
                        conversation_id: conversationId,
                    },
                    body: {
                        label: trimmedLabel,
                    },
                });
                await mutate();
                toast.success('Annotation added', {
                    persistNotification: false,
                });
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to add annotation', {
                        persistNotification: false,
                    });
                }
            }
        },
        [conversationId, mutate],
    );

    // "l" opens the annotation picker (vim/gmail-style label shortcut), but only
    // while this panel is the visible sidebar tab. preventDefault stops the "l"
    // from also landing in the search box the picker autofocuses when it opens.
    useGlobalHotkey(
        'l',
        (event) => {
            event.preventDefault();
            setAnnotationPickerOpen(true);
        },
        { enabled: shortcutsEnabled },
    );

    const handleDeleteAnnotation = useCallback(
        async (annotationId: string) => {
            setDeletingAnnotationIds((current) => {
                const next = new Set(current);
                next.add(annotationId);
                return next;
            });
            try {
                await getApiClient().delete({
                    url: '/admin/agent-traces/{conversation_id}/review-annotations/{annotation_id}',
                    pathParams: {
                        conversation_id: conversationId,
                        annotation_id: annotationId,
                    },
                });
                await mutate();
                toast.success('Annotation deleted', {
                    persistNotification: false,
                });
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to delete annotation', {
                        persistNotification: false,
                    });
                }
            } finally {
                setDeletingAnnotationIds((current) => {
                    const next = new Set(current);
                    next.delete(annotationId);
                    return next;
                });
            }
        },
        [conversationId, mutate],
    );

    const annotationSuggestions = annotationGroupsData?.groups ?? [];
    const humanAnnotations = trace.review.annotations.filter(
        (annotation) => !isAgentTraceJudgeAnnotation(annotation),
    );
    // Any label already on the trace — human OR judge — shows as applied, so the
    // picker never invites a duplicate create that the backend would silently
    // de-dupe (leaving the checkmark unchanged and the user retrying).
    const selectedAnnotationLabels = new Set(
        trace.review.annotations.map((annotation) => annotation.label),
    );
    // Toggling adds a label or removes the human annotation that applied it.
    // A label applied only by the judge is shown as applied but isn't removable
    // here, so re-selecting it is a no-op rather than a phantom create. A label
    // with a request already in flight is ignored until mutate() settles, so a
    // rapid second pick can't duplicate the POST or race a delete vs create.
    const handleToggleAnnotationLabel = (label: string) => {
        if (pendingAnnotationLabels.has(label)) {
            return;
        }
        const existingHumanAnnotation = humanAnnotations.find(
            (annotation) => annotation.label === label,
        );
        const isJudgeOnlyLabel =
            !existingHumanAnnotation && selectedAnnotationLabels.has(label);
        if (isJudgeOnlyLabel) {
            return;
        }
        const mutation = existingHumanAnnotation
            ? handleDeleteAnnotation(existingHumanAnnotation.id)
            : handleSelectAnnotationLabel(label);
        setPendingAnnotationLabels((current) => new Set(current).add(label));
        void mutation.finally(() => {
            setPendingAnnotationLabels((current) => {
                const next = new Set(current);
                next.delete(label);
                return next;
            });
        });
    };
    const humanReviewNotes = trace.review.notes.filter(
        (note) => note.source_type !== AGENT_TRACE_LLM_JUDGE_REVIEW_SOURCE,
    );
    const linkedLinearIssues: Array<AgentTraceLinearIssue> =
        trace.review.linked_linear_issues;

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div
                className={cn(
                    'flex flex-1 flex-col gap-4 pr-1',
                    AGENT_TRACE_DETAIL_SCROLL_REGION_CLASS_NAME,
                )}
            >
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <CardTitle className="text-sm">Review</CardTitle>
                            <Badge
                                variant="secondary"
                                className={cn(
                                    'text-xs',
                                    getReviewStatusBadgeClassName(
                                        trace.review.review_status,
                                    ),
                                )}
                            >
                                {
                                    REVIEW_STATUS_LABELS[
                                        trace.review.review_status
                                    ]
                                }
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="grid gap-3 text-xs sm:grid-cols-2">
                            <div>
                                <div className="text-muted-foreground font-medium">
                                    Assignee
                                </div>
                                <div className="mt-1 truncate">
                                    {trace.review.owner_user_name ??
                                        'Unassigned'}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground font-medium">
                                    Linear
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {linkedLinearIssues.length > 0 ? (
                                        linkedLinearIssues.map((issue) => (
                                            <a
                                                key={issue.identifier}
                                                href={issue.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary inline-flex items-center gap-1 underline"
                                            >
                                                {issue.identifier}
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        ))
                                    ) : (
                                        <span className="text-muted-foreground">
                                            None linked
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-fit"
                            onClick={onTriageWithAdminAgent}
                        >
                            <Sparkles className="mr-2 h-4 w-4" />
                            Draft triage with Admin Agent
                        </Button>
                        <div className="grid gap-2">
                            <label className="text-muted-foreground text-xs font-medium">
                                Disposition
                            </label>
                            <Select
                                value={disposition}
                                onValueChange={(value) => {
                                    if (isAgentTraceReviewDisposition(value)) {
                                        setDisposition(value);
                                    }
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {REVIEW_DISPOSITION_OPTIONS.map(
                                        (option) => (
                                            <SelectItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </SelectItem>
                                        ),
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-muted-foreground text-xs font-medium">
                                Final human diagnosis
                            </label>
                            <Textarea
                                value={finalDiagnosisDraft}
                                onChange={(event) =>
                                    setFinalDiagnosisDraft(event.target.value)
                                }
                                className="min-h-[96px] text-sm"
                                placeholder="Override the judge/Admin Agent diagnosis with the final human call."
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-muted-foreground text-xs font-medium">
                                Link Linear issue
                            </label>
                            <div className="grid gap-2 sm:grid-cols-[minmax(0,8rem)_minmax(0,1fr)]">
                                <Input
                                    value={linearIdentifierDraft}
                                    onChange={(event) =>
                                        setLinearIdentifierDraft(
                                            event.target.value,
                                        )
                                    }
                                    placeholder="PHO-12345"
                                />
                                <Input
                                    value={linearUrlDraft}
                                    onChange={(event) =>
                                        setLinearUrlDraft(event.target.value)
                                    }
                                    placeholder="https://linear.app/..."
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleLinkLinearIssue}
                                disabled={isSavingReview}
                            >
                                {isSavingReview ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Link2 className="mr-2 h-4 w-4" />
                                )}
                                Link issue
                            </Button>
                            <Button
                                type="button"
                                onClick={handleMarkReviewed}
                                disabled={isSavingReview}
                            >
                                {isSavingReview ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                )}
                                Mark done
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">Notes</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <Textarea
                                value={noteDraft}
                                onChange={(event) =>
                                    setNoteDraft(event.target.value)
                                }
                                className="text-sm"
                                placeholder="Capture review context, follow-ups, or findings…"
                            />
                            <div className="flex justify-end">
                                <Button
                                    onClick={handleCreateNote}
                                    disabled={
                                        isCreatingNote || !noteDraft.trim()
                                    }
                                >
                                    {isCreatingNote ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : null}
                                    Add Note
                                </Button>
                            </div>
                        </div>
                        {humanReviewNotes.length === 0 ? null : (
                            <div className="flex flex-col gap-4 pr-1">
                                {humanReviewNotes.map((note) => (
                                    <div
                                        key={note.id}
                                        className="rounded-lg border p-3"
                                    >
                                        <div className="text-muted-foreground mb-2 flex flex-wrap items-center gap-2 text-xs">
                                            <span>
                                                {note.source_name ??
                                                    note.source_type}
                                            </span>
                                            <span>
                                                {format(
                                                    new Date(note.created_at),
                                                    'MMM d, h:mm a',
                                                )}
                                            </span>
                                        </div>
                                        <div className="text-sm whitespace-pre-wrap">
                                            {note.note}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">Annotations</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <AnnotationCombobox
                            open={annotationPickerOpen}
                            onOpenChange={setAnnotationPickerOpen}
                            suggestions={annotationSuggestions}
                            selectedLabels={selectedAnnotationLabels}
                            isLoading={isAnnotationGroupsLoading}
                            onToggleLabel={handleToggleAnnotationLabel}
                        />
                        {humanAnnotations.length === 0 ? null : (
                            <div className="grid gap-2">
                                <div className="text-muted-foreground text-xs font-medium">
                                    Human annotations
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {humanAnnotations.map((annotation) => {
                                        const isDeleting =
                                            deletingAnnotationIds.has(
                                                annotation.id,
                                            );
                                        return (
                                            <Badge
                                                key={annotation.id}
                                                variant="outline"
                                                className="gap-1 pr-1"
                                            >
                                                <span>{annotation.label}</span>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleDeleteAnnotation(
                                                            annotation.id,
                                                        )
                                                    }
                                                    disabled={isDeleting}
                                                    aria-label={`Delete annotation ${annotation.label}`}
                                                    className="hover:bg-muted rounded p-0.5 disabled:opacity-50"
                                                >
                                                    {isDeleting ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <X className="h-3 w-3" />
                                                    )}
                                                </button>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export function TurnCard({
    turn,
    runStartedAt,
    entityAliases,
    organizationId,
    mode,
}: {
    readonly turn: AgentTraceTurnRead;
    readonly runStartedAt: string;
    readonly entityAliases: AgentTraceDetail['entity_aliases'];
    readonly organizationId: string;
    readonly mode: Mode;
}) {
    const [open, setOpen] = useState(true);
    const elapsedLabel = formatElapsedMs(
        new Date(turn.created_at).getTime() - new Date(runStartedAt).getTime(),
    );

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <Card>
                <CollapsibleTrigger asChild={true}>
                    <CardHeader className="cursor-pointer py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                                {open ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                                <span className="font-medium">
                                    Turn {turn.sequence}
                                </span>
                                {turn.model && (
                                    <Badge
                                        variant="outline"
                                        className="text-xs"
                                    >
                                        {turn.model}
                                    </Badge>
                                )}
                                {turn.status && (
                                    <Badge
                                        variant="secondary"
                                        className="text-xs"
                                    >
                                        {turn.status}
                                    </Badge>
                                )}
                                {turn.incomplete_reason && (
                                    <Badge
                                        variant="secondary"
                                        className="bg-yellow-100 text-xs text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300"
                                    >
                                        {turn.incomplete_reason ===
                                        'max_output_tokens'
                                            ? 'Truncated — output token limit'
                                            : `Incomplete — ${turn.incomplete_reason}`}
                                    </Badge>
                                )}
                                {(turn.input_tokens !== null ||
                                    turn.output_tokens !== null ||
                                    turn.cost_usd !== null) && (
                                    <span className="text-muted-foreground text-xs">
                                        {String(turn.input_tokens ?? 0)} in /{' '}
                                        {String(turn.output_tokens ?? 0)} out ·{' '}
                                        {turn.cost_usd === null
                                            ? '—'
                                            : `$${Number(turn.cost_usd).toFixed(
                                                  4,
                                              )}`}{' '}
                                        cost
                                    </span>
                                )}
                            </div>
                            <span className="text-muted-foreground text-xs">
                                {elapsedLabel}
                                {' · '}
                                {format(new Date(turn.created_at), 'h:mm:ss a')}
                            </span>
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="flex flex-col gap-2 pt-0">
                        {turn.items.map((item) => (
                            <ItemRow
                                key={item.id}
                                item={item}
                                entityAliases={entityAliases}
                                organizationId={organizationId}
                                mode={mode}
                            />
                        ))}
                    </CardContent>
                </CollapsibleContent>
            </Card>
        </Collapsible>
    );
}

export function ItemRow({
    item,
    entityAliases,
    organizationId,
    mode,
}: {
    readonly item: AgentTraceItemRead;
    readonly entityAliases: AgentTraceDetail['entity_aliases'];
    readonly organizationId: string;
    readonly mode: Mode;
}) {
    if (item.type === 'text') {
        const isUser = item.direction === 'input';
        return (
            <div
                className={cn(
                    'rounded-lg px-3 py-2 text-sm',
                    isUser ? 'bg-blue-50 dark:bg-blue-950' : 'bg-muted/50',
                )}
            >
                <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-xs font-medium">
                    {isUser ? (
                        <User className="h-3 w-3" />
                    ) : (
                        <Bot className="h-3 w-3" />
                    )}
                    {isUser ? 'User' : 'Assistant'}
                </div>
                <ModelSpaceContent
                    text={item.text_content ?? ''}
                    entityAliases={entityAliases}
                    organizationId={organizationId}
                    mode={mode}
                />
            </div>
        );
    }

    if (item.type === 'function_call') {
        return <ToolCallItem item={item} />;
    }

    if (item.type === 'function_output') {
        return (
            <ToolOutputItem
                item={item}
                entityAliases={entityAliases}
                organizationId={organizationId}
                mode={mode}
            />
        );
    }

    if (item.type === 'reasoning') {
        return (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs dark:bg-amber-950">
                <span className="font-medium text-amber-700 dark:text-amber-300">
                    Reasoning
                </span>
                <div className="text-muted-foreground mt-1">
                    <ModelSpaceContent
                        text={item.text_content ?? ''}
                        entityAliases={entityAliases}
                        organizationId={organizationId}
                        mode={mode}
                    />
                </div>
            </div>
        );
    }

    if (item.type === 'compaction') {
        return (
            <div className="bg-muted/60 rounded-lg px-3 py-2 text-xs">
                <div className="text-muted-foreground mb-1 flex items-center gap-1.5 font-medium">
                    <Sparkles className="h-3 w-3" />
                    Compaction summary
                </div>
                <div className="text-muted-foreground">
                    <ModelSpaceContent
                        text={item.text_content ?? ''}
                        entityAliases={entityAliases}
                        organizationId={organizationId}
                        mode={mode}
                    />
                </div>
            </div>
        );
    }

    if (item.text_content === null) {
        return null;
    }
    return (
        <div className="bg-muted/60 rounded-lg px-3 py-2 text-xs">
            <div className="text-muted-foreground mb-1 font-medium">
                {item.type}
            </div>
            <ModelSpaceContent
                text={item.text_content}
                entityAliases={entityAliases}
                organizationId={organizationId}
                mode={mode}
            />
        </div>
    );
}

function CopyContentButton({ content }: { readonly content: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        await navigator.clipboard.writeText(content);
        setCopied(true);
    }, [content]);

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
                void handleCopy();
            }}
            className="text-muted-foreground hover:text-foreground absolute top-1 right-1 h-6 w-6 opacity-0 transition-opacity group-hover/pre:opacity-100"
            aria-label="Copy stored content"
            title="Copy to clipboard"
        >
            {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
                <Copy className="h-3.5 w-3.5" />
            )}
        </Button>
    );
}

function sortJsonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(sortJsonValue);
    }
    if (!isRecord(value)) {
        return value;
    }
    return Object.fromEntries(
        Object.keys(value)
            .sort()
            .map((key) => [key, sortJsonValue(value[key])]),
    );
}

function prettyPrintStableJson(value: unknown): string {
    return JSON.stringify(sortJsonValue(value), null, 2) ?? 'null';
}

function ToolCallItem({ item }: { readonly item: AgentTraceItemRead }) {
    const [open, setOpen] = useState(false);
    const content = prettyPrintStableJson(item.arguments_json);

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <div className="rounded-lg border px-3 py-2">
                <CollapsibleTrigger asChild={true}>
                    <div className="flex cursor-pointer items-center gap-2 text-xs">
                        {open ? (
                            <ChevronDown className="h-3 w-3" />
                        ) : (
                            <ChevronRight className="h-3 w-3" />
                        )}
                        <Wrench className="h-3 w-3 text-violet-600" />
                        <span className="font-mono font-medium">
                            {item.tool_name ?? 'unknown'}
                        </span>
                        {item.tool_status && (
                            <Badge
                                variant={
                                    item.tool_status === 'success'
                                        ? 'default'
                                        : 'destructive'
                                }
                                className="text-[10px]"
                            >
                                {item.tool_status}
                            </Badge>
                        )}
                    </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="group/pre relative mt-2">
                        <CopyContentButton content={content} />
                        <pre
                            className="bg-muted max-h-[300px] overflow-auto rounded p-2 text-[11px]"
                            data-testid="stored-tool-content"
                        >
                            {content}
                        </pre>
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}

function ToolOutputItem({
    item,
    entityAliases,
    organizationId,
    mode,
}: {
    readonly item: AgentTraceItemRead;
    readonly entityAliases: AgentTraceDetail['entity_aliases'];
    readonly organizationId: string;
    readonly mode: Mode;
}) {
    const [open, setOpen] = useState(false);
    const hasJsonOutput =
        item.output_json !== null && item.output_json !== undefined;
    // Denied/failed tool calls persist an empty output body with the reason
    // only in raw_json — surface the error instead of a bare "{}".
    const rawError =
        typeof item.raw_json === 'object' &&
        item.raw_json !== null &&
        'error' in item.raw_json &&
        typeof item.raw_json.error === 'string'
            ? item.raw_json.error
            : null;
    const showRawError =
        rawError !== null &&
        hasJsonOutput &&
        typeof item.output_json === 'object' &&
        item.output_json !== null &&
        Object.keys(item.output_json).length === 0;
    const showJson = hasJsonOutput && !showRawError;
    const content = showRawError
        ? rawError
        : showJson
          ? prettyPrintStableJson(item.output_json)
          : (item.text_content ?? '');

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <div className="rounded-lg border border-dashed px-3 py-2">
                <CollapsibleTrigger asChild={true}>
                    <div className="flex cursor-pointer items-center gap-2 text-xs">
                        {open ? (
                            <ChevronDown className="h-3 w-3" />
                        ) : (
                            <ChevronRight className="h-3 w-3" />
                        )}
                        <span className="text-muted-foreground">
                            Output:{' '}
                            <span className="font-mono">
                                {item.tool_name ?? 'unknown'}
                            </span>
                        </span>
                    </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="group/pre relative mt-2">
                        <CopyContentButton content={content} />
                        <pre
                            className="bg-muted max-h-[300px] overflow-auto rounded p-2 text-[11px]"
                            data-testid="stored-tool-content"
                        >
                            {showJson ? (
                                content
                            ) : (
                                <ModelSpaceContent
                                    text={content}
                                    entityAliases={entityAliases}
                                    organizationId={organizationId}
                                    mode={mode}
                                />
                            )}
                        </pre>
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}
